import React, { Fragment, useContext, useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Divider, Box, GridLegacy as Grid, IconButton, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";
import { differenceInHours, isAfter } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";

import NotchedCard from "../../../components/cards/NotchedCard/NotchedCard";
import EditableField, {
  EditableFieldTypes,
} from "../../../components/editable/EditableField/EditableField";
import EditableGroup, {
  EditableGroupType,
} from "../../../components/editable/EditableGroup/EditableGroup";
import EditableContainer from "../../../components/editable/EditableContainer/EditableContainer";
import { CharacterCasing } from "../../../components/editable/EditableField/fields/TextInputField";
import { useCustomerLookUp } from "../../../components/dialogs";
import { BookingBrandLogo } from "../../../components/brand/BookingBrandLogo";

import {
  ReleaseFlag,
  useReleaseManagement,
} from "../../../utility/feature-management";
import { validateTrackingIdFormat } from "@/utility/validation";

import FlightLookup from "../BookingView/components/FlightLookup/FlightLookup";
import BookingMovement from "../BookingMovement/BookingMovement";

import {
  Booking,
  BookingStatus,
  IBookingCreate,
  Terminal,
  VehicleStatus,
  PaymentTypes,
  Contact,
  AssetVehicle,
  IContactCreate,
  IAssetVehicleCreate,
  Flight,
  ICreateFlight,
  SystemProfile,
  AssetLocationTypes,
  api,
  KeyMovementStatus,
  KeyMovementInfo,
  BookingRemark,
  RemarkType,
  FuelType,
  LuggageType,
  LaneAllocationSystem,
  IBookingSalesAndPaymentsResult,
  ProductSaleItem,
  CleanAirComplianceStatus,
} from "../../../logic/api";

import styles from "../styles";
import flex from "../../../styles/flex";

import { SessionContext } from "../../../utility/useSession";
import { usePreferences } from "../../../utility/usePreferences";
import { useDate } from "../../../utility/useDate";

import { isAssetVehicleDVLAResult } from "../../../types/guards";
import ConfirmationDialog from "../../../components/notifications/confirmationDialog/ConfirmationDialog";
import AlertDialog from "../../../components/notifications/alertDialog/AlertDialog";
import ErrorDialog from "../../../components/notifications/errorDialog/ErrorDialog";
import { countVehicleIssues } from "../../../utility/assetIssues";
import { useTranslation } from "react-i18next";
import input from "./booking-fields.module.css";
import { promisifyApi } from "../../../utility/utils";
import NotificationBanner from "./NotificationBanner";
import { useBookingDays } from "../../../hooks/useBookingDays";
import { IDropDownListItem } from "../../../components/editable/EditableField/fields/DropDownField";
import { useUserPermissions } from "@/hooks/useUserPermissions";
import BookingHeaderPanel from "../BookingView/components/BookingHeaderPanel";
import BookingHeaderPanelSkeleton from "../BookingView/components/BookingHeaderPanelSkeleton";
import { calculateDefaultBookingEndDate } from "../../../utility/defaultBookingDuration";

enum ReturnFlightConfirmation {
  None = "none",
  Late = "late",
  Early = "early",
}

interface IValidationDialogContent {
  info?: Error;
  title: string;
}

const fuelTypeOptions = [
  { name: "Petrol", value: FuelType.Petrol },
  { name: "Diesel", value: FuelType.Diesel },
  { name: "Electric", value: FuelType.Electric },
  { name: "Unknown", value: FuelType.Unknown },
];

const cleanAirComplianceOptions = [
  { name: "Compliant", value: CleanAirComplianceStatus.Compliant },
  { name: "Non-Compliant", value: CleanAirComplianceStatus.NonCompliant },
  { name: "Unknown", value: CleanAirComplianceStatus.Unknown },
];

const luggageOptions = [
  { name: "Hand", value: LuggageType.Hand },
  { name: "Hold", value: LuggageType.Hold },
  { name: "Oversize", value: LuggageType.Oversize },
];

function isBookingObject(booking: Booking | IBookingCreate): booking is Booking {
  return "id" in booking;
}

const BookingFields: React.FunctionComponent<{
  readOnly: boolean;
  booking: Booking | IBookingCreate;
  remarks?: BookingRemark[];
  remarksSectionRef?: React.RefObject<HTMLDivElement>;
  isNew: boolean;
  saveBooking: (data: Booking | IBookingCreate) => Promise<boolean>;
  movement?: (data: Booking) => Promise<boolean>;
  showComplaints?: () => void;
  hideNotificationBanners?: boolean;
  saleItems?: ProductSaleItem[];
  salesAndPayments?: IBookingSalesAndPaymentsResult;
  bookingId?: number;
  paymentsSectionRef?: React.RefObject<HTMLDivElement>;
  onCompletedListUpdated?: () => void;
  onRemarkTypeChange?: (type: RemarkType) => void;
  headerPanelReleased?: boolean;
  isLoadingSalesAndPayments?: boolean;
}> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const { hasPermission } = useUserPermissions();
  const {
    usesPaymentMethod,
    usesDVLALookUp,
    usesCustomerLookUp,
    isRecordPropertyVisible,
    isRecordPropertyMandatory,
    isBookingEndTimeLocked,
    defaultBookingDuration,
    defaultBookingDurationUsesMidnight,
    timeZone,
  } = usePreferences();
  const defaultBookingEndAppliedRef = useRef(false);
  const [booking, setBookingState] = useState<Booking | IBookingCreate>(
    props.booking
  );
  const [lastSeenDate, setLastSeenDate] = useState<string | null>(null);

  // Only update if vehicleStatus or booking id changes to avoid overriding user input
  useEffect(() => {
    const currentBooking = booking as Booking;
    const newBooking = props.booking as Booking;

    // Only sync if this is a different booking or the vehicle status changed (check-in/out or undo check-in/out)
    // or if movement dates changed
    if (
      currentBooking?.id !== newBooking?.id ||
      currentBooking?.vehicle?.statusId !== newBooking?.vehicle?.statusId ||
      currentBooking?.vehicle?.movementDate !== newBooking?.vehicle?.movementDate ||
      currentBooking?.vehicleKey?.movementDate !== newBooking?.vehicleKey?.movementDate
    ) {
      setBookingState(props.booking);
    }
  }, [
    (props.booking as Booking)?.id,
    (props.booking as Booking)?.vehicle?.statusId,
    (props.booking as Booking)?.vehicle?.movementDate,
    (props.booking as Booking)?.vehicleKey?.movementDate,
  ]);

  const {
    setCustomerLookUpDialogIsOpen,
    setCustomerLastName,
    setVehicleRegistration,
    customerLookUpSelection,
    clearCustomerLookUpSelection,
  } = useCustomerLookUp();
  const customerLookUpEnabled = usesCustomerLookUp();
  const { toFormattedDateTimeString, localizeDate } = useDate();
  const editableBookingCardFlag = useReleaseManagement(
    ReleaseFlag.releaseEditableBookingCard
  );
  const isDatePickerReleased = useReleaseManagement(
    ReleaseFlag.releaseBookingDatepicker
  );
  const notesForDriversFlag = useReleaseManagement(
    ReleaseFlag.releaseNotesForDrivers
  );
  const isVehicleMovementAssumeKeyholderReleased = useReleaseManagement(
    ReleaseFlag.releaseVehicleMovementAssumeKeyholder
  );

  const vehicleInReturnsBeforeKeyFlag = useReleaseManagement(
    ReleaseFlag.releaseVehicleInReturnsBeforeKey
  );

  const additionalCustomizableFieldsFlag = useReleaseManagement(
    ReleaseFlag.releaseAdditionalCustomizableFields
  );
  const isImproveContactRecordOptionsReleased = useReleaseManagement(
    ReleaseFlag.releaseImproveContactRecordOptions
  );
  const kmosManualAllocationReleased = useReleaseManagement(
    ReleaseFlag.kmosManualAllocationReleased
  );
  const customerRemarksBannerReleased = useReleaseManagement(
    ReleaseFlag.releaseCustomerRemarksBanner
  );
  const isStoreFuelTypeReleased = useReleaseManagement(
    ReleaseFlag.releaseStoreFuelType
  );
  const dateKeyBoxReleased = useReleaseManagement(
    ReleaseFlag.releaseDateKeyBox
  );
  const isSydneyDualParkAllocationReleased = useReleaseManagement(
    ReleaseFlag.releaseSydneyDualParkAllocation
  );
  const isTruncateVehicleFieldsReleased = useReleaseManagement(
    ReleaseFlag.releaseTruncateVehicleFields
  );
  const isMobileNumberSupportsAusFormat = useReleaseManagement(
    ReleaseFlag.releaseMobileNumberSupportsAusFormat
  );
  const releaseAdditionalExternalReferences = useReleaseManagement(
    ReleaseFlag.releaseAdditionalExternalReferences
  );
  const releaseImproveFlightLookup = useReleaseManagement(
    ReleaseFlag.releaseImproveFlightLookup
  );
  const isBrandNameEnabled = useReleaseManagement(
    ReleaseFlag.releaseBrandName
  );
  const isBookingLastSeenDateReleased = useReleaseManagement(
    ReleaseFlag.releaseBookingLastSeenDate
  );
  const isFlightRecordOptionsReleased = useReleaseManagement(
    ReleaseFlag.releaseFlightRecordOptions
  );
  const isCollectionDeliveryPointsReleased = useReleaseManagement(
    ReleaseFlag.releaseCollectionDeliveryPoints
  );
  const isAddLuggageFieldToBookingReleased = useReleaseManagement(
    ReleaseFlag.releaseAddLuggageFieldToBooking
  );
  const preventInvalidBookingDatesFlag = useReleaseManagement(
    ReleaseFlag.releasePreventInvalidBookingDates
  );

  const isReleaseShuttleBusPickupRequest = useReleaseManagement(ReleaseFlag.releaseShuttleBusPickupRequest);
  const isCollectionDeliveryMeetGreetReleased = useReleaseManagement(ReleaseFlag.releaseCollectionDeliveryMeetGreet);
  const isPreventAmendmentArrivalAndReturnTimesReleased = useReleaseManagement(ReleaseFlag.releasePreventAmendmentArrivalAndReturnTimes);
  const isRbacPermissionEditingBookingStartAndEndDateReleased = useReleaseManagement(ReleaseFlag.releaseRbacPermissionEditingBookingStartAndEndDate);

  const isShuttlePickupEnabled =
    session.current.site?.preferences?.request_shuttle_pickup_enabled === true;

  const isDisplayCleanAirComplianceReleased =
    useReleaseManagement(ReleaseFlag.releaseDisplayCleanAirCompliance);

  const showShuttlePickupRequest =
    isReleaseShuttleBusPickupRequest && isShuttlePickupEnabled;

  const keyMovementRulesAPI = promisifyApi(
    api.hooks.assetMovement.getKeyMovementRules()
  );
  const validateTrackingIdAPI = promisifyApi(
    api.hooks.booking.validateTrackingIdExternalReference()
  );
  const setTrackingIdAPI = promisifyApi(api.hooks.booking.setTrackingId());
  const getMeetAndGreetLocationsAPI = api.hooks.assetLocation.getMeetAndGreetLocations();

  const location = useLocation();
  const isNewBookingPage = location.pathname.endsWith("new-booking");
  const bookingDays = useBookingDays(booking as Booking);
  const bookingCarParkId = booking.carPark?.id ?? session.current.site?.home.id ?? 0;
  const [meetAndGreetLocations, setMeetAndGreetLocations] = useState<{
    collection: string[];
    delivery: string[];
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    setMeetAndGreetLocations(null);

    if (!bookingCarParkId || !isCollectionDeliveryPointsReleased) {
      setMeetAndGreetLocations({ collection: [], delivery: [] });
      return () => {
        cancelled = true;
      };
    }

    getMeetAndGreetLocationsAPI.send(
      session.current.context,
      { carParkId: bookingCarParkId },
      (locations) => {
        if (!cancelled) {
          setMeetAndGreetLocations(locations ?? { collection: [], delivery: [] });
        }
      },
    );

    return () => {
      cancelled = true;
    };
  }, [bookingCarParkId, isCollectionDeliveryPointsReleased]);

  const getBookingPointOptions = (
    pointType: "collection" | "delivery",
    savedValue: string | null | undefined,
  ): IDropDownListItem[] => {
    const names = meetAndGreetLocations?.[pointType] ?? [];
    const options = [{ name: "", value: "" }, ...names.map((name) => ({ name, value: name }))];

    if (!props.isNew && savedValue && !names.includes(savedValue)) {
      options.splice(1, 0, {
        name: t("booking.labels.previouslyEntered", { value: savedValue }),
        value: savedValue,
      });
    }

    return options;
  };

  const requestBookingPointOptions = (
    pointType: "collection" | "delivery",
    savedValue: string | null | undefined,
  ): Promise<IDropDownListItem[]> =>
    meetAndGreetLocations == null
      ? new Promise(() => { })
      : Promise.resolve(getBookingPointOptions(pointType, savedValue));

  const { kmosVersion, kmosManualAllocationEnabled, useTrackingId, trackingIdRegex } =
    usePreferences();
  const kmosV2 = kmosVersion === "2.0";
  const isTrackingIdValidationReleased = useReleaseManagement(ReleaseFlag.releaseTrackingIdValidation);
  const isKmosManualAllocationEnabled =
    kmosManualAllocationReleased &&
    kmosV2 === true &&
    kmosManualAllocationEnabled === true;
  const isDualParkSystemEnabled =
    isSydneyDualParkAllocationReleased &&
    session.current.site?.preferences.lane_allocation_system ===
    LaneAllocationSystem.DualPark;
  const showLaneAllocationFields =
    isKmosManualAllocationEnabled || isDualParkSystemEnabled;

  const findManyByVehicleOrLeadContactAPI = promisifyApi(api.hooks.booking.findManyByVehicleOrLeadContact());

  if (!session.current.site) {
    return <Fragment />;
  }

  // Determines the edit mode based on whether a booking is being created or edited, and on user permissions
  let editMode = props.isNew
    ? EditableGroupType.alwaysEditing
    : hasPermission("bookingManagement.booking.update")
      ? EditableGroupType.switchableEditing
      : EditableGroupType.none;

  let vehicleStatus = VehicleStatus.NotCheckedIn;
  let bookingStatus = BookingStatus.Active;
  let otherCarPark = "";

  if (!props.isNew) {
    const existingBooking = booking as Booking;

    vehicleStatus = existingBooking.vehicle.statusId;
    bookingStatus = existingBooking.statusId;

    if (vehicleStatus == VehicleStatus.CheckedOut) {
      editMode = EditableGroupType.none;
    } else if (bookingStatus == BookingStatus.Cancelled) {
      editMode = EditableGroupType.none;
    }

    if (existingBooking.carPark != null) {
      if (existingBooking.carPark.id != session.current.site.home.id) {
        editMode = EditableGroupType.none;
        otherCarPark = existingBooking.carPark.name;
      }
    }
  }

  const preferences = session.current.site.preferences;

  if (props.readOnly) {
    editMode = EditableGroupType.none;
  }

  useEffect(() => {
    if (editMode !== EditableGroupType.alwaysEditing && isBookingLastSeenDateReleased && preferences.show_customer_last_seen_date) {
      findManyByVehicleOrLeadContactAPI.send(session.current.context, {
        registration: booking.vehicle.registration,
        lastName: booking.leadContact.lastName
      }).then(data => {
        if (data && data.length > 0) {
          const lastSeenDate = toFormattedDateTimeString(new Date(data[0].vehicle.checkOutDate));

          setLastSeenDate(lastSeenDate);
        }
      });
    }
  }, []);

  useEffect(() => {
    if (customerLookUpEnabled && customerLookUpSelection) {
      setBooking({
        ...booking,
        // @ts-expect-error due to `accountNumber` being typed differently across
        // the Contact & IContactCreate types (one is string | null, the other is string | undefined)
        leadContact: {
          ...booking.leadContact,
          ...customerLookUpSelection.leadContact,
        },
        vehicle: {
          ...booking.vehicle,
          ...customerLookUpSelection.vehicle,
        },
        vip: customerLookUpSelection.vip,
        assistance: customerLookUpSelection.assistance,
        paxCount: customerLookUpSelection.paxCount,
      });
      clearCustomerLookUpSelection();
    }
  }, [customerLookUpSelection]);

  const openCustomerLookUpDialog = () => {
    if (!customerLookUpEnabled) {
      return;
    }

    setCustomerLastName(booking.leadContact.lastName || "");
    setVehicleRegistration(booking.vehicle.registration);
    setCustomerLookUpDialogIsOpen(true);
  };

  const hasValidDateValue = (value: unknown): boolean => {
    return typeof value === "string" && value.length > 0 && !isNaN(Date.parse(value));
  };

  const setBooking = (booking: Booking | IBookingCreate): void => {
    const defaultBookingEndDate = calculateDefaultBookingEndDate(
      booking.bookingStartDate,
      defaultBookingDuration,
      defaultBookingDurationUsesMidnight,
      timeZone,
    );
    const hasReturnFlightDate = hasValidDateValue(booking.returnFlight?.flightDate);

    if (
      booking.isWalkIn &&
      hasReturnFlightDate &&
      defaultBookingEndAppliedRef.current === true
    ) {
      booking.bookingEndDate = undefined;
      defaultBookingEndAppliedRef.current = false;
    } else if (
      booking.isWalkIn &&
      defaultBookingEndDate &&
      !hasReturnFlightDate &&
      (
        !hasValidDateValue(booking.bookingEndDate) ||
        defaultBookingEndAppliedRef.current === true
      )
    ) {
      booking.bookingEndDate = defaultBookingEndDate;
      defaultBookingEndAppliedRef.current = true;
    }

    if (
      booking.isWalkIn &&
      defaultBookingEndAppliedRef.current !== true &&
      isBookingEndTimeLocked &&
      booking.bookingStartDate &&
      booking.bookingEndDate &&
      !isNaN(Date.parse(booking.bookingStartDate)) &&
      !isNaN(Date.parse(booking.bookingEndDate))
    ) {
      const startDate = new Date(booking.bookingStartDate);
      const endDate = new Date(booking.bookingEndDate);
      endDate.setHours(startDate.getHours());
      endDate.setMinutes(startDate.getMinutes());
      endDate.setSeconds(startDate.getSeconds());
      endDate.setMilliseconds(startDate.getMilliseconds());
      booking.bookingEndDate = localizeDate(endDate).toISO() ?? undefined;
    }

    setBookingState(booking);
  };

  useEffect(() => {
    const defaultBookingEndDate = calculateDefaultBookingEndDate(
      booking.bookingStartDate,
      defaultBookingDuration,
      defaultBookingDurationUsesMidnight,
      timeZone,
    );

    if (
      booking.isWalkIn &&
      defaultBookingEndDate &&
      !hasValidDateValue(booking.returnFlight?.flightDate) &&
      (
        !hasValidDateValue(booking.bookingEndDate) ||
        defaultBookingEndAppliedRef.current === true
      ) &&
      booking.bookingEndDate !== defaultBookingEndDate
    ) {
      setBooking({ ...booking });
    }
  }, [
    defaultBookingDuration,
    defaultBookingDurationUsesMidnight,
    timeZone,
    booking.bookingStartDate,
    booking.returnFlight?.flightDate,
  ]);

  const setLeadContact = (contact: Partial<Contact | IContactCreate>): void => {
    setBooking({
      ...booking,
      // @ts-expect-error `accountNumber` is typed differently across `Contact` and `IContactCreate`
      leadContact: {
        ...booking.leadContact,
        ...contact,
      },
    });
  };

  const setVehicle = (
    vehicle: Partial<AssetVehicle | IAssetVehicleCreate>
  ): void => {
    // @ts-expect-error `id` is typed differently across `AssetVehicle` and `IAssetVehicleCreate`
    setBooking({ ...booking, vehicle: { ...booking.vehicle, ...vehicle } });
  };

  const setOutboundFlight = (flight: Partial<Flight | ICreateFlight>): void => {
    setBooking({
      ...booking,
      // @ts-expect-error `flightDate` is typed differently across `Flight` and `ICreateFlight`
      outboundFlight: {
        ...booking.outboundFlight,
        ...flight,
      },
    });
  };

  const setReturnFlight = useCallback(
    (flight: Partial<Flight | ICreateFlight>): void => {
      setBooking({
        ...booking,
        // @ts-expect-error `flightDate` is typed differently across `Flight` and `ICreateFlight`
        returnFlight: {
          ...booking.returnFlight,
          ...flight,
        },
      });
    },
    [booking]
  );

  const setConfirmedArrivalDate = useCallback(
    (confirmedArrivalDate: string): void => {
      setBooking({
        ...booking,
        confirmedArrivalDate: new Date(confirmedArrivalDate)
      });
    },
    [booking]
  );

  const setConfirmedReturnDate = useCallback(
    (confirmedReturnDate: string): void => {
      setBooking({
        ...booking,
        confirmedReturnDate: new Date(confirmedReturnDate),
      });
    },
    [booking]
  );

  const currentDate = new Date();

  const bookingStartDate = booking.bookingStartDate
    ? new Date(booking.bookingStartDate)
    : undefined;
  const bookingEndDate = booking.bookingEndDate
    ? new Date(booking.bookingEndDate)
    : undefined;
  const outboundFlightDate = booking.outboundFlight.flightDate
    ? new Date(booking.outboundFlight.flightDate)
    : undefined;
  const returnFlightDate = booking.returnFlight.flightDate
    ? new Date(booking.returnFlight.flightDate)
    : undefined;
  const canUpdateBookingDates = hasPermission(
    "bookingManagement.booking.updateBookingDates"
  );
  const isLegacyBookingCardEditable =
    editableBookingCardFlag &&
    booking.isWalkIn &&
    vehicleStatus < VehicleStatus.CheckedIn;
  // Gates the Start/End date and time fields specifically. When the flag is off this
  // matches the legacy card-level condition exactly, since dates had no separate gate before.
  const canEditBookingDates =
    props.isNew ||
    (isRbacPermissionEditingBookingStartAndEndDateReleased
      ? canUpdateBookingDates
      : isLegacyBookingCardEditable);
  const isBookingCardEditable =
    props.isNew || isLegacyBookingCardEditable || canEditBookingDates
      ? editMode
      : EditableGroupType.none;
  const bookingFlightTimeDifference =
    bookingEndDate && returnFlightDate
      ? Math.floor(
        Math.abs(bookingEndDate.getTime() - returnFlightDate.getTime()) /
        3600000
      )
      : undefined;

  const handleBookingFlightDateAdjustments = (startDate: string) => {
    if (startDate) {
      if (!bookingEndDate || new Date(startDate) < bookingEndDate) {
        bookingStartDate &&
          setBooking({ ...booking, bookingStartDate: startDate });
      }
      if (bookingEndDate && new Date(startDate) > bookingEndDate) {
        setBooking({
          ...booking,
          bookingEndDate: startDate,
          bookingStartDate: startDate,
        });
      }
    }
  };

  interface DialogStateProps {
    show: boolean;
    onClose?: (result: boolean) => void;
  }

  const [showLateFlightReturnDialog, setShowLateFlightReturnDialog] =
    useState<DialogStateProps>({ show: false, onClose: () => undefined });
  const [showEarlyFlightReturnDialog, setShowEarlyFlightReturnDialog] =
    useState<DialogStateProps>({ show: false, onClose: () => undefined });
  const [bookingDatesChangedDialog, setBookingDatesChangedDialog] =
    useState<{ show: boolean; message: string }>({ show: false, message: "" });
  const [validationWarningDialogContent, setValidationWarningDialogContent] =
    useState<IValidationDialogContent>({ title: "" });
  const [validationWarningDialog, setValidationWarningDialog] =
    useState<DialogStateProps>({ show: false, onClose: () => undefined });
  const [validationErrorDialogContent, setValidationErrorDialogContent] =
    useState<IValidationDialogContent>({ title: "" });
  const [showLocationOverfilledDialog, setLocationOverfilledDialog] =
    useState<DialogStateProps>({ show: false, onClose: () => undefined });
  const [returnFlightConfirmation, setReturnFlightConfirmation] =
    useState<ReturnFlightConfirmation>(ReturnFlightConfirmation.None);

  const getDatePortion = useCallback((dateString?: string | null) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toISOString().split("T")[0];
  }, []);

  const hasBookingDateChanged = (
    newDate?: string | null,
    previousDate?: string | null
  ): boolean => {
    if (!newDate && !previousDate) return false;
    if (!newDate || !previousDate) return true;
    return new Date(newDate).getTime() !== new Date(previousDate).getTime();
  };

  const notifyBookingDatesChanged = (data: Booking | IBookingCreate): void => {
    if (props.isNew || !isRbacPermissionEditingBookingStartAndEndDateReleased) {
      return;
    }

    const startChanged = hasBookingDateChanged(
      data.bookingStartDate,
      props.booking.bookingStartDate
    );
    const endChanged = hasBookingDateChanged(
      data.bookingEndDate,
      props.booking.bookingEndDate
    );

    if (!startChanged && !endChanged) return;

    setBookingDatesChangedDialog({
      show: true,
      message:
        startChanged && endChanged
          ? t("input.messages.bookingDatesChanged")
          : startChanged
            ? t("input.messages.bookingStartDateChanged")
            : t("input.messages.bookingEndDateChanged"),
    });
  };

  // Reset confirmation when return flight date changes
  useEffect(() => {
    const currentDate = getDatePortion(booking.returnFlight.flightDate);
    const previousDate = getDatePortion(
      props.booking.returnFlight.flightDate
    );

    if (currentDate !== previousDate) {
      setReturnFlightConfirmation(ReturnFlightConfirmation.None);
    }
  }, [
    booking.returnFlight.flightDate,
    props.booking.returnFlight.flightDate,
    getDatePortion,
  ]);

  const isLateReturn = useCallback(() => {
    if (!returnFlightDate || !bookingEndDate) return false;
    return isAfter(returnFlightDate, bookingEndDate);
  }, [returnFlightDate, bookingEndDate]);

  const isEarlyReturn = useCallback(() => {
    if (!returnFlightDate || !bookingEndDate) return false;
    return differenceInHours(bookingEndDate, returnFlightDate) >= 6;
  }, [returnFlightDate, bookingEndDate]);

  const isAltRefRequired = useCallback(() => {
    return (
      !booking.altRef &&
      session.current.site?.preferences.bookingRecordOptions?.properties
        ?.altRef?.required &&
      (booking as Booking).id
    );
  }, [
    booking.altRef,
    session.current.site?.preferences.bookingRecordOptions?.properties?.altRef
      ?.required,
    (booking as Booking).id,
  ]);

  const adjustReturnFlightToBookingEnd = () => {
    const adjustedBooking = {
      ...booking,
      returnFlight: {
        ...booking.returnFlight,
        flightDate: booking.bookingEndDate,
      },
    };
    // @ts-expect-error `flightDate` is typed differently across `Flight` and `ICreateFlight`
    setBooking(adjustedBooking);
    // @ts-expect-error `flightDate` is typed differently across `Flight` and `ICreateFlight`
    props.saveBooking(adjustedBooking);
  };

  const handleLateReturnConfirmation = useCallback(async () => {
    return new Promise<boolean>((accept) => {
      const onClosePopup = async (result: boolean): Promise<void> => {
        if (result) {
          // User confirmed prompt
          setReturnFlightConfirmation(ReturnFlightConfirmation.Late);
        } else {
          // User cancelled prompt, revert to previous state or to booking end time
          setReturnFlightConfirmation(ReturnFlightConfirmation.None);
          if (!booking.returnFlight.flightDate) {
            setBooking(props.booking);
          } else {
            adjustReturnFlightToBookingEnd();
          }
        }
        const saved = result ? await props.saveBooking(booking) : false;
        setShowLateFlightReturnDialog({
          show: false,
          onClose: () => undefined,
        });
        accept(saved);
      };
      setShowLateFlightReturnDialog({ show: true, onClose: onClosePopup });
    });
  }, [booking, props.booking, props.saveBooking]);

  const handleEarlyReturnConfirmation = useCallback(async () => {
    return new Promise<boolean>((accept) => {
      const onClosePopup = async (result: boolean): Promise<void> => {
        if (result) {
          // User confirmed prompt
          setReturnFlightConfirmation(ReturnFlightConfirmation.Early);
        } else {
          // User cancelled prompt, revert to previous state or to booking end time
          setReturnFlightConfirmation(ReturnFlightConfirmation.None);
          if (!booking.returnFlight.flightDate) {
            setBooking(props.booking);
          } else {
            adjustReturnFlightToBookingEnd();
          }
        }
        const saved = result ? await props.saveBooking(booking) : false;
        setShowEarlyFlightReturnDialog({
          show: false,
          onClose: () => undefined,
        });
        accept(saved);
      };
      setShowEarlyFlightReturnDialog({ show: true, onClose: onClosePopup });
    });
  }, [booking, props.booking, props.saveBooking]);

  const validateFlightUpdate = useCallback(
    async (data: Booking | IBookingCreate): Promise<boolean> => {
      // Skip validation if no return flight date
      if (!data.returnFlight.flightDate) {
        return props.saveBooking(data);
      }

      const datesCanBeCompared = returnFlightDate && bookingEndDate;

      // Check for late or early return and show appropriate dialog
      if (datesCanBeCompared) {
        if (
          isLateReturn() &&
          returnFlightConfirmation !== ReturnFlightConfirmation.Late
        ) {
          return handleLateReturnConfirmation();
        } else if (
          isEarlyReturn() &&
          returnFlightConfirmation !== ReturnFlightConfirmation.Early
        ) {
          return handleEarlyReturnConfirmation();
        }
      }

      // Check for alt ref requirement
      if (isAltRefRequired()) {
        setValidationErrorDialogContent({
          info: new Error(t("label.validations.noAltRef")),
          title: "",
        });
        return false;
      }

      return props.saveBooking(data);
    },
    [
      bookingEndDate,
      returnFlightDate,
      isLateReturn,
      isEarlyReturn,
      returnFlightConfirmation,
      handleLateReturnConfirmation,
      handleEarlyReturnConfirmation,
      isAltRefRequired,
      props.saveBooking,
      t,
    ]
  );

  const validateBookingDates = async (data: Booking | IBookingCreate): Promise<boolean> => {
    if (!preventInvalidBookingDatesFlag) {
      return true;
    }

    const startDate = data.bookingStartDate
      ? new Date(data.bookingStartDate)
      : null;
    const endDate = data.bookingEndDate
      ? new Date(data.bookingEndDate)
      : null;

    if (!startDate || !endDate) {
      return true;
    }

    if (endDate < startDate) {
      setValidationErrorDialogContent({
        info: new Error(t("errors.bookingStartDateAfterEndDate")),
        title: "",
      });
      return false;
    }

    return true;
  };

  const validateMovement = async (
    data: Booking,
    originalBooking: Booking
  ): Promise<boolean> => {
    const vehicleLocation = data.vehicle.location;
    const keyLocation = data.vehicleKey.location;

    if (!keyLocation) return true;

    const isVehicleLocationAtMaxCapacity =
      vehicleLocation &&
      vehicleLocation.capacity > 0 &&
      vehicleLocation.alertIfOverfilled &&
      vehicleLocation.vehicleCountTotal >= vehicleLocation.capacity;

    const isKeyLocationAtMaxCapacity =
      keyLocation &&
      keyLocation.capacity > 0 &&
      keyLocation.alertIfOverfilled &&
      keyLocation.keyCountTotal >= keyLocation.capacity;

    if (dateKeyBoxReleased) {
      const validationResults = await keyMovementRulesAPI.send(
        session.current.context,
        { locationId: keyLocation.id, vehicle: data.vehicle }
      );

      // blocking cases
      if (validationResults?.keyMovementStatus === KeyMovementStatus.block) {
        const blockErrors: Record<KeyMovementInfo, string> = {
          [KeyMovementInfo.zoneNotAllowed]: "errors.keyMovementBlockedZone",
          [KeyMovementInfo.wrongDate]: "errors.keyMovementBlockedDate",
          [KeyMovementInfo.wrongTimeSlot]:
            "errors.keyMovementBlockedDateTimeslot",
          [KeyMovementInfo.differentAssignedLane]:
            "errors.keyMovementBlockedAllocation",
          [KeyMovementInfo.noAssignedLane]:
            "errors.keyMovementBlockedAllocation",
          [KeyMovementInfo.allowed]: "",
        };

        if (
          validationResults.keyMovementInfo &&
          validationResults.keyMovementInfo in blockErrors
        ) {
          const errorKey =
            blockErrors[
            validationResults.keyMovementInfo as KeyMovementInfo
            ];
          setValidationErrorDialogContent({
            info: new Error(t(errorKey)),
            title: t("messages.keyMovementBlocked"),
          });
          return false;
        }
      }

      // warning cases
      if (validationResults?.keyMovementStatus === KeyMovementStatus.alert) {
        const warningErrors: Record<KeyMovementInfo, string> = {
          [KeyMovementInfo.zoneNotAllowed]: "errors.keyMovementBlockedZone",
          [KeyMovementInfo.wrongDate]: "errors.keyMovementBlockedDate",
          [KeyMovementInfo.wrongTimeSlot]:
            "errors.keyMovementBlockedDateTimeslot",
          [KeyMovementInfo.differentAssignedLane]:
            "errors.keyMovementBlockedAllocation",
          [KeyMovementInfo.noAssignedLane]:
            "errors.keyMovementBlockedAllocation",
          [KeyMovementInfo.allowed]: "",
        };

        if (
          validationResults.keyMovementInfo &&
          validationResults.keyMovementInfo in warningErrors
        ) {
          const warningKey =
            warningErrors[
            validationResults.keyMovementInfo as KeyMovementInfo
            ];

          // First show date/time slot warning
          const confirmed = await new Promise<boolean>((accept) => {
            const onClose = (result: boolean): void => {
              setValidationWarningDialog({
                show: false,
                onClose: () => undefined,
              });
              accept(result);
            };

            setValidationWarningDialogContent({
              info: new Error(t(warningKey)),
              title: t("messages.keyMovementWarning"),
            });
            setValidationWarningDialog({ show: true, onClose });
          });

          if (!confirmed) {
            setBooking(props.booking);
            return false;
          }
        }
      }
    }

    // check for capacity
    if (isVehicleLocationAtMaxCapacity || isKeyLocationAtMaxCapacity) {
      const capacityConfirmed = await new Promise<boolean>((accept) => {
        const onClosePopup = (result: boolean): void => {
          setLocationOverfilledDialog({ show: false, onClose: () => undefined });
          accept(result);
        };
        setLocationOverfilledDialog({ show: true, onClose: onClosePopup });
      });

      if (!capacityConfirmed) {
        setBooking(props.booking);
        return false;
      }
    }

    // Check returns validation
    const vehicleInReturnsBeforeKeySetting =
      preferences.workflowRules?.vehicleInReturnsBeforeKey ?? false;
    if (vehicleInReturnsBeforeKeyFlag && vehicleInReturnsBeforeKeySetting) {
      const vehicleIsInReturns = vehicleLocation?.returns;
      if (
        !vehicleIsInReturns &&
        keyLocation?.returns &&
        !originalBooking.vehicle.location?.returns &&
        !originalBooking.vehicleKey.location?.returns
      ) {
        setValidationErrorDialogContent({
          info: new Error(
            t("errors.vehicleInReturnsBeforeKey", {
              location: keyLocation?.name,
            })
          ),
          title: t("messages.keyMovementBlocked"),
        });
        return false;
      }
    }

    // If all validations pass, execute the movement
    if (props.movement) {
      await props.movement(data);
    }

    return true;
  };

  const validateTrackingIdOnSave = async (
    data: Booking
  ): Promise<Booking | null> => {
    if (!data.externalRefs?.length) {
      return data;
    }

    const trackingIdRef = data.externalRefs[0];
    if (!trackingIdRef) {
      return data;
    }

    if (typeof trackingIdRef.ref === "string" && !trackingIdRef.id) {
      // If tracking ID is empty, clear it by calling setTrackingId without trackingId
      if (trackingIdRef.ref.trim() === "") {
        try {
          const setTrackingIdResult = await setTrackingIdAPI.send(
            session.current.context,
            { bookingId: data.id }
          );

          if (setTrackingIdResult) {
            setBooking(setTrackingIdResult);
            return setTrackingIdResult;
          }

          return data;
        } catch (error) {
          console.error("Failed to clear tracking ID:", error);
          setValidationErrorDialogContent({
            title: t("general.error"),
            info: new Error("Failed to clear tracking ID"),
          });
          return null;
        }
      } else {
        // If tracking ID has content, validate format first,then set it
        if (isTrackingIdValidationReleased && !validateTrackingIdFormat(trackingIdRef.ref, trackingIdRegex)) {
          setValidationErrorDialogContent({
            title: t("general.error"),
            info: new Error(t("settings.invalidTrackingId")),
          });
          return null;
        }

        try {
          const validateResult = await validateTrackingIdAPI.send(
            session.current.context,
            { bookingId: data.id, ref: trackingIdRef.ref }
          );

          if (validateResult?.success && validateResult.data) {
            const updatedData = { ...data };

            if (!updatedData.externalRefs) {
              updatedData.externalRefs = [];
            }

            updatedData.externalRefs[0] = validateResult.data;

            const setTrackingIdResult = await setTrackingIdAPI.send(
              session.current.context,
              { bookingId: updatedData.id, trackingId: validateResult.data }
            );

            if (setTrackingIdResult) {
              setBooking(setTrackingIdResult);
              return setTrackingIdResult;
            }

            return updatedData;
          }

          if (validateResult?.error) {
            setValidationErrorDialogContent({
              title: t("general.error"),
              info: new Error(validateResult?.error),
            });
            return null;
          }
        } catch (error) {
          console.error("Failed to validate tracking ID:", error);
          return null;
        }
      }
    }

    // No change required (existing tracking id) or nothing to validate
    return data;
  };

  const isBooking = (
    booking: Booking | IBookingCreate
  ): booking is Booking => {
    if (booking.hasOwnProperty("vehicleAuditIssues")) {
      return true;
    }

    return false;
  };

  const getVehicleIssuesString = (booking: Booking | IBookingCreate) => {
    if (isBooking(booking)) {
      const vic = countVehicleIssues(booking.vehicleAuditIssues);

      const vehicleIssueString = vic
        ? `${vic} ISSUE${vic > 1 ? "S" : ""} FOUND`
        : "";
      return vehicleIssueString;
    }

    return "";
  };

  const contactRecordOptions =
    session.current.site.preferences.contactRecordOptions;
  const bookingRecordOptions =
    session.current.site.preferences.bookingRecordOptions;
  const customerCard = (
    <NotchedCard style={styles.card} label={t("booking.cards.customer")}>
      {isBookingLastSeenDateReleased && preferences.show_customer_last_seen_date && lastSeenDate && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            backgroundColor: "background.paper",
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
          >
            {t("dialogs.lastSeen", { date: lastSeenDate })}
          </Typography>
        </Box>
      )}
      <EditableGroup<Booking | IBookingCreate>
        id="customer"
        testId="customer-group"
        editType={editMode}
        data={booking}
        onValidate={(data) => {
          // Title
          if (
            isImproveContactRecordOptionsReleased &&
            isRecordPropertyVisible(contactRecordOptions, "title") &&
            isRecordPropertyMandatory(contactRecordOptions, "title") &&
            !data.leadContact.title
          ) {
            setValidationErrorDialogContent({
              info: new Error(t("input.validations.noTitle")),
              title: "",
            });
            return Promise.resolve(false);
          }

          // Last name (required by default)
          if (!data.leadContact.lastName?.trim()) {
            setValidationErrorDialogContent({
              info: new Error(t("input.validations.contactSurname")),
              title: "",
            });
            return Promise.resolve(false);
          }

          // First name / initial
          if (
            isImproveContactRecordOptionsReleased &&
            (contactRecordOptions?.properties.firstName?.visible ?? true) &&
            isRecordPropertyMandatory(contactRecordOptions, "firstName")
          ) {
            const displayedValue = preferences.booking_leadContact_firstName_enabled
              ? data.leadContact.firstName
              : data.leadContact.initial;

            if (!displayedValue) {
              setValidationErrorDialogContent({
                info: new Error(
                  t(
                    preferences.booking_leadContact_firstName_enabled
                      ? "input.validations.noFirstName"
                      : "input.validations.noInitial"
                  )
                ),
                title: "",
              });
              return Promise.resolve(false);
            }
          }

          // Passenger count
          if (
            isRecordPropertyVisible(contactRecordOptions, "passengers") &&
            (!isImproveContactRecordOptionsReleased ||
              isRecordPropertyMandatory(contactRecordOptions, "passengers"))
          ) {
            // If field is mandatory, it must have a value greater than 0
            if (
              data.paxCount === null ||
              data.paxCount === undefined ||
              Number.isNaN(data.paxCount) ||
              data.paxCount < 1
            ) {
              setValidationErrorDialogContent({
                info: new Error(
                  t("input.validations.passengerCountInvalid")
                ),
                title: "",
              });
              return Promise.resolve(false);
            }
          }

          if (additionalCustomizableFieldsFlag || isImproveContactRecordOptionsReleased) {
            // Email
            if (
              isRecordPropertyMandatory(contactRecordOptions, "email") &&
              !data.leadContact.email?.trim()
            ) {
              setValidationErrorDialogContent({
                info: new Error(t("input.validations.noEmail")),
                title: "",
              });
              return Promise.resolve(false);
            }

            // Mobile
            if (
              isRecordPropertyMandatory(contactRecordOptions, "mobile") &&
              !data.leadContact.phoneMobile?.trim()
            ) {
              setValidationErrorDialogContent({
                info: new Error(t("input.validations.noMobile")),
                title: "",
              });
              return Promise.resolve(false);
            }
          }

          // Luggage
          if (
            isAddLuggageFieldToBookingReleased &&
            isRecordPropertyVisible(bookingRecordOptions, "luggage") &&
            isRecordPropertyMandatory(bookingRecordOptions, "luggage") &&
            data.luggage == null
          ) {
            setValidationErrorDialogContent({
              info: new Error(t("input.validations.noLuggage")),
              title: "",
            });
            return Promise.resolve(false);
          }

          return Promise.resolve(true);
        }}
        onDataChanged={props.saveBooking}
        onCancelEdit={() => setBooking(props.booking)}
      >
        <Grid container>
          {preferences.booking_leadContact_title_enabled == true &&
            (preferences.booking_leadContact_title_dropdown == true ? (
              <EditableField<Booking | IBookingCreate, string>
                inputType={EditableFieldTypes.title}
                label={t("booking.labels.title")}
                casing={CharacterCasing.upper}
                required={isRecordPropertyMandatory(contactRecordOptions, "title")}
                maxLength={10}
                onProvide={() => booking.leadContact.title}
                onChange={(data, title) => setLeadContact({ title })}
              />
            ) : (
              <EditableField<Booking | IBookingCreate, string>
                inputType={EditableFieldTypes.text}
                casing={CharacterCasing.upper}
                label={t("booking.labels.title")}
                required={isRecordPropertyMandatory(contactRecordOptions, "title")}
                maxLength={10}
                onProvide={() => booking.leadContact.title}
                onChange={(data, title) => setLeadContact({ title })}
              />
            ))}

          {(!isImproveContactRecordOptionsReleased || (contactRecordOptions?.properties.firstName?.visible ?? true)) && (
            preferences.booking_leadContact_firstName_enabled == true ? (
              <EditableField<Booking | IBookingCreate, string>
                inputType={EditableFieldTypes.text}
                label={t("booking.labels.firstName")}
                required={isImproveContactRecordOptionsReleased && Boolean(contactRecordOptions?.properties.firstName?.required)}
                maxLength={50}
                testId="booking-firstname-input"
                onProvide={() => booking.leadContact.firstName || ""}
                onChange={(data, value) => {
                  setLeadContact({
                    firstName: value || "",
                    initial: value?.substring(0, 1),
                  });
                }}
              />
            ) : (
              <EditableField<Booking | IBookingCreate, string>
                inputType={EditableFieldTypes.text}
                label={t("booking.labels.initial")}
                required={isImproveContactRecordOptionsReleased && Boolean(contactRecordOptions?.properties.firstName?.required)}
                maxLength={1}
                onProvide={() => booking.leadContact.initial}
                onChange={(data, initial) => setLeadContact({ initial })}
              />
            )
          )}

          <EditableField<Booking | IBookingCreate, string>
            inputType={EditableFieldTypes.text}
            label={`${t("booking.labels.lastName")}`}
            required
            maxLength={50}
            testId="booking-surname-input"
            onProvide={() => booking.leadContact.lastName || ""}
            onChange={(data, lastName) => {
              setLeadContact({ lastName });
              setCustomerLastName(lastName);
            }}
            iconButton={
              customerLookUpEnabled && (
                <IconButton
                  onClick={openCustomerLookUpDialog}
                  style={{ padding: 0, width: "auto", height: "auto" }}
                  aria-label="search"
                  disableRipple
                >
                  <Search />
                </IconButton>
              )
            }
            onEnterKeyPress={openCustomerLookUpDialog}
          />

          {(isRecordPropertyVisible(contactRecordOptions, "passengers")) && (
            <EditableField<Booking | IBookingCreate, number>
              inputType={EditableFieldTypes.number}
              label={t("booking.labels.paxCount")}
              required={isRecordPropertyMandatory(
                contactRecordOptions,
                "passengers"
              )}
              onProvide={() => booking.paxCount}
              onChange={(data, paxCount) =>
                setBooking({ ...booking, paxCount })
              }
            />
          )}

          {(!additionalCustomizableFieldsFlag || isRecordPropertyVisible(contactRecordOptions, "mobile")) && (
            isMobileNumberSupportsAusFormat ? (
              <EditableField<Booking, string>
                inputType={EditableFieldTypes.phoneNumber}
                label={t("booking.labels.phoneMobile")}
                required={
                  (additionalCustomizableFieldsFlag || isImproveContactRecordOptionsReleased) &&
                  isRecordPropertyMandatory(contactRecordOptions, "mobile")
                }
                onProvide={() => booking.leadContact.phoneMobile || ""}
                onChange={(data, phoneMobile) =>
                  setLeadContact({ phoneMobile })
                }
              />
            ) : (
              <EditableField<Booking, string>
                inputType={EditableFieldTypes.text}
                maxLength={14}
                label={t("booking.labels.phoneMobile")}
                required={
                  (additionalCustomizableFieldsFlag || isImproveContactRecordOptionsReleased) &&
                  isRecordPropertyMandatory(contactRecordOptions, "mobile")
                }
                onProvide={() => booking.leadContact.phoneMobile || ""}
                onChange={(data, phoneMobile) =>
                  setLeadContact({ phoneMobile })
                }
              />
            )
          )}

          {isAddLuggageFieldToBookingReleased &&
            isRecordPropertyVisible(bookingRecordOptions, "luggage") && (
              <EditableField<Booking | IBookingCreate, IDropDownListItem>
                inputType={EditableFieldTypes.enumDropDownField}
                label={t("booking.labels.luggage")}
                required={isRecordPropertyMandatory(
                  bookingRecordOptions,
                  "luggage"
                )}
                onRequestData={() =>
                  Promise.resolve([
                    { name: "", value: "" },
                    ...luggageOptions.map((option) => ({
                      name: t(
                        `booking.luggageTypes.${option.name.toLowerCase()}`
                      ),
                      value: option.value.toString(),
                    })),
                  ])
                }
                onProvide={() => {
                  const luggage = (booking as Booking).luggage;
                  const option = luggageOptions.find(
                    (opt) => opt.value.toString() === luggage?.toString()
                  );
                  return {
                    name: option
                      ? t(
                        `booking.luggageTypes.${option.name.toLowerCase()}`
                      )
                      : "",
                    value: option ? option.value.toString() : "",
                  };
                }}
                onChange={(data, selectedOption) => {
                  setBooking({
                    ...booking,
                    luggage: selectedOption.value
                      ? parseInt(selectedOption.value)
                      : null,
                  });
                }}
              />
            )}

          {usesPaymentMethod(PaymentTypes.account) && (
            <EditableField<Booking, string>
              inputType={EditableFieldTypes.text}
              maxLength={99}
              label={t("booking.labels.accountNumber")}
              readOnly={vehicleStatus === VehicleStatus.CheckedOut}
              onProvide={() => booking.leadContact.accountNumber || ""}
              onChange={(data, accountNumber) =>
                setLeadContact({ accountNumber })
              }
            />
          )}

          {(!additionalCustomizableFieldsFlag || isRecordPropertyVisible(contactRecordOptions, "email")) && (
            <EditableField<Booking, string>
              inputType={EditableFieldTypes.text}
              maxLength={1000}
              label={t("booking.labels.email")}
              required={
                (additionalCustomizableFieldsFlag || isImproveContactRecordOptionsReleased) &&
                isRecordPropertyMandatory(contactRecordOptions, "email")
              }
              onProvide={() => booking.leadContact.email || ""}
              onChange={(data, email) => setLeadContact({ email })}
              copyable
            />
          )}

          {preferences.booking_leadContact_title_enabled != true && (
            <div style={flex.flexFill} />
          )}

          {usesPaymentMethod(PaymentTypes.account) && (
            <div style={flex.flexFill} />
          )}

          <EditableField<Booking | IBookingCreate, boolean>
            inputType={EditableFieldTypes.switch}
            label={t("booking.labels.vip")}
            onProvide={() => Boolean(booking.vip)}
            onChange={(data, vip) => setBooking({ ...booking, vip })}
          />

          <EditableField<Booking | IBookingCreate, boolean>
            inputType={EditableFieldTypes.switch}
            label={t("booking.labels.assistance")}
            onProvide={() => Boolean(booking.assistance)}
            onChange={(data, assistance) =>
              setBooking({ ...booking, assistance })
            }
          />
        </Grid>
      </EditableGroup>
    </NotchedCard>
  );

  const vehicleRecordOptions =
    session.current.site.preferences.vehicleRecordOptions;
  const vehicleCard = (
    <>
      {isBooking(booking) && countVehicleIssues(booking.vehicleAuditIssues) ? (
        <div
          style={{
            marginBottom: -21,
            marginLeft: 326,
            border: "1px solid red",
            borderRadius: 4,
            fontSize: 16,
            backgroundColor: "white",
            width: 150,
            fontWeight: 700,
            color: "red",
            zIndex: 1000000,
            position: "relative",
          }}
        >
          {getVehicleIssuesString(booking)}
        </div>
      ) : null}
      <NotchedCard style={styles.card} label={t("booking.cards.vehicle")}>
        <div className={input.container}>
          <EditableGroup<Booking | IBookingCreate>
            id="vehicle"
            testId="vehicle-group"
            editType={editMode}
            data={booking}
            onValidate={(data) => {

              // vehicle registration
              if (
                data.vehicle.registration == null ||
                String(data.vehicle.registration).trim() === ""
              ) {
                setValidationErrorDialogContent({
                  info: new Error(
                    t("input.validations.noVehicleRegistration")
                  ),
                  title: "",
                });
                return Promise.resolve(false);
              }

              // vehicle model
              if (
                isRecordPropertyMandatory(vehicleRecordOptions, "model") &&
                !data.vehicle.model
              ) {
                setValidationErrorDialogContent({
                  info: new Error(t("input.validations.noVehicleModel")),
                  title: "",
                });
                return Promise.resolve(false);
              }

              // vehicle make
              if (
                additionalCustomizableFieldsFlag &&
                isRecordPropertyMandatory(vehicleRecordOptions, "make") &&
                !data.vehicle.make
              ) {
                setValidationErrorDialogContent({
                  info: new Error(t("input.validations.noVehicleMake")),
                  title: "",
                });
                return Promise.resolve(false);
              }

              // vehicle colour
              if (
                additionalCustomizableFieldsFlag &&
                isRecordPropertyMandatory(vehicleRecordOptions, "colour") &&
                !data.vehicle.colour
              ) {
                setValidationErrorDialogContent({
                  info: new Error(t("input.validations.noVehicleColour")),
                  title: "",
                });
                return Promise.resolve(false);
              }

              // vehicle fuel type
              if (
                isStoreFuelTypeReleased &&
                isRecordPropertyMandatory(vehicleRecordOptions, "fuelType") &&
                (data.vehicle.fuelType === undefined ||
                  data.vehicle.fuelType === null)
              ) {
                setValidationErrorDialogContent({
                  info: new Error(t("input.validations.noVehicleFuelType")),
                  title: "",
                });
                return Promise.resolve(false);
              }

              // clean air compliance
              if (
                isDisplayCleanAirComplianceReleased &&
                isRecordPropertyMandatory(vehicleRecordOptions, "cleanAirCompliance") &&
                (data.vehicle.cleanAirCompliant == null)
              ) {
                setValidationErrorDialogContent({
                  info: new Error(t("input.validations.noVehicleCleanAirCompliance")),
                  title: "",
                });
                return Promise.resolve(false);
              }

              return Promise.resolve(true);
            }}
            onDataChanged={props.saveBooking}
            onCancelEdit={() => setBooking(props.booking)}
          >
            {/* TODO: PKI-9531 Temporary fix for the vehicle fields */}
            <Grid container spacing={isTruncateVehicleFieldsReleased ? 1 : 0}>
              <div className={input.item}>
                {preferences.system_profile == SystemProfile.fleetManagement && (
                  <EditableField<Booking | IBookingCreate, string>
                    inputType={EditableFieldTypes.text}
                    useFullWidthWhenEditing={true}
                    columnWidth={12}
                    maxLength={20}
                    label={t("input.labels.vin")}
                    onProvide={() => booking.vehicle.vin ?? ""}
                    onChange={(data, vin) => setVehicle({ vin })}
                  />
                )}
                <EditableField<Booking | IBookingCreate, string>
                  inputType={
                    preferences.booking_vehicle_dvla_enabled
                      ? EditableFieldTypes.dvla
                      : EditableFieldTypes.text
                  }
                  useFullWidthWhenEditing={true}
                  useFullWidthWhenViewing={true}
                  columnWidth={12}
                  label={t("booking.labels.registration")}
                  required
                  maxLength={15}
                  testId="booking-registration-input"
                  onProvide={() => booking.vehicle.registration}
                  onChange={(data, value: unknown) => {
                    if (typeof value === "string") {
                      data.vehicle.registration = value;
                      data.vehicle.make = "";
                      data.vehicle.model = "";
                      data.vehicle.colour = "";
                      data.vehicle.fuelType = FuelType.Unknown;
                      data.vehicle.sourceFuelType = "";
                      data.vehicle.sourceProvider = "";
                      data.vehicle.cleanAirCompliant = null;
                      setVehicle({ registration: value });
                      setVehicleRegistration(value);

                      return;
                    }

                    if (usesDVLALookUp() && isAssetVehicleDVLAResult(value)) {
                      data.vehicle.registration = value.registration;
                      data.vehicle.make = value.make;
                      data.vehicle.model = value.model;
                      data.vehicle.colour = value.colour;
                      if (isStoreFuelTypeReleased) {
                        data.vehicle.fuelType = value.fuelType;
                        data.vehicle.sourceFuelType = value.sourceFuelType;
                        data.vehicle.sourceProvider = value.sourceProvider;
                      }
                      if (isDisplayCleanAirComplianceReleased) {
                        data.vehicle.cleanAirCompliant = value.cleanAirCompliant;
                      }

                      setVehicle({
                        registration: value.registration,
                        make: value.make,
                        model: value.model,
                        colour: value.colour,
                        ...(isStoreFuelTypeReleased && {
                          fuelType: value.fuelType,
                          sourceFuelType: value.sourceFuelType,
                          sourceProvider: value.sourceProvider,
                        }),
                        ...(isDisplayCleanAirComplianceReleased && {
                          cleanAirCompliant: value.cleanAirCompliant,
                        }),
                      });
                      setVehicleRegistration(value.registration);
                    }
                  }}
                />

                {(!additionalCustomizableFieldsFlag || isRecordPropertyVisible(vehicleRecordOptions, "colour")) && (
                  <EditableField<Booking | IBookingCreate, string>
                    inputType={EditableFieldTypes.text}
                    columnWidth={12}
                    maxLength={20}
                    useFullWidthWhenEditing={true}
                    // TODO: PKI-9531 Temporary fix for the vehicle fields
                    useFullWidthWhenViewing={
                      isTruncateVehicleFieldsReleased ? true : false
                    }
                    label={t("booking.labels.colour")}
                    required={
                      additionalCustomizableFieldsFlag &&
                      isRecordPropertyMandatory(
                        vehicleRecordOptions,
                        "colour"
                      )
                    }
                    onProvide={() => booking.vehicle.colour}
                    onChange={(data, colour) => setVehicle({ colour })}
                  />
                )}

                {((!additionalCustomizableFieldsFlag || isRecordPropertyVisible(vehicleRecordOptions, "cleanAirCompliance")) && isDisplayCleanAirComplianceReleased) && (
                  <EditableField<Booking | IBookingCreate, IDropDownListItem>
                    inputType={EditableFieldTypes.enumDropDownField}
                    useFullWidthWhenEditing={true}
                    useFullWidthWhenViewing={
                      isTruncateVehicleFieldsReleased ? true : false
                    }
                    maxLength={20}
                    columnWidth={12}
                    label={t("booking.ulezCompliance.label")}
                    required={
                      additionalCustomizableFieldsFlag &&
                      isRecordPropertyMandatory(
                        vehicleRecordOptions,
                        "cleanAirCompliance"
                      )
                    }
                    onRequestData={() =>
                      Promise.resolve([
                        { name: "", value: "" },
                        ...cleanAirComplianceOptions.map((option) => ({
                          name: option
                            ? t(
                              `booking.ulezCompliance.${option.name.toLowerCase()}`
                            )
                            : "-",
                          value: option ? option.value?.toString() : "",
                        })),
                      ])
                    }
                    onProvide={() => {
                      const cleanAirCompliance = booking.vehicle.cleanAirCompliant;
                      const option = cleanAirComplianceOptions.find(
                        (opt) =>
                          opt.value?.toString() === cleanAirCompliance?.toString()
                      );
                      return {
                        name: option
                          ? t(
                            `booking.ulezCompliance.${option.name.toLowerCase()}`
                          )
                          : "-",
                        value: option ? option.value?.toString() : "",
                      };
                    }}
                    onChange={(data, selectedOption) => {
                      const cleanAirComplianceValue = selectedOption?.value
                        ? parseInt(selectedOption.value)
                        : null;
                      setVehicle({
                        cleanAirCompliant: selectedOption?.value ? cleanAirComplianceValue : null,
                      });
                    }}
                  />
                )}
              </div>
              <div className={input.item}>
                {(!additionalCustomizableFieldsFlag || isRecordPropertyVisible(vehicleRecordOptions, "make")) && (
                  <EditableField<Booking | IBookingCreate, string>
                    inputType={EditableFieldTypes.text}
                    useFullWidthWhenEditing={true}
                    // TODO: PKI-9531 Temporary fix for the vehicle fields
                    useFullWidthWhenViewing={
                      isTruncateVehicleFieldsReleased ? true : false
                    }
                    maxLength={20}
                    columnWidth={12}
                    label={t("booking.labels.make")}
                    required={
                      additionalCustomizableFieldsFlag &&
                      isRecordPropertyMandatory(
                        vehicleRecordOptions,
                        "make"
                      )
                    }
                    onProvide={() => booking.vehicle.make}
                    onChange={(data, make) => setVehicle({ make })}
                  />
                )}

                {isRecordPropertyVisible(vehicleRecordOptions, "model") && (
                  <EditableField<Booking | IBookingCreate, string>
                    inputType={EditableFieldTypes.text}
                    useFullWidthWhenEditing={true}
                    // TODO: PKI-9531 Temporary fix for the vehicle fields
                    useFullWidthWhenViewing={
                      isTruncateVehicleFieldsReleased ? true : false
                    }
                    maxLength={20}
                    columnWidth={12}
                    label={t("booking.labels.model")}
                    required={isRecordPropertyMandatory(
                      vehicleRecordOptions,
                      "model"
                    )}
                    onProvide={() => booking.vehicle.model}
                    onChange={(data, model) => setVehicle({ model })}
                  />
                )}

                {isStoreFuelTypeReleased &&
                  isRecordPropertyVisible(vehicleRecordOptions, "fuelType") && (
                    <EditableField<
                      Booking | IBookingCreate,
                      IDropDownListItem
                    >
                      inputType={EditableFieldTypes.enumDropDownField}
                      columnWidth={12}
                      copyable
                      label={t("booking.labels.fuelType")}
                      required={isRecordPropertyMandatory(
                        vehicleRecordOptions,
                        "fuelType"
                      )}
                      useFullWidthWhenEditing={true}
                      onRequestData={() =>
                        Promise.resolve([
                          { name: "", value: "" },
                          ...fuelTypeOptions.map((option) => ({
                            name: t(
                              `booking.fuelTypes.${option.name.toLowerCase()}`
                            ),
                            value: option.value.toString(),
                          })),
                        ])
                      }
                      onProvide={() => {
                        const fuelType = booking.vehicle.fuelType;
                        const option = fuelTypeOptions.find(
                          (opt) =>
                            opt.value?.toString() === fuelType?.toString()
                        );
                        return {
                          name: option
                            ? t(
                              `booking.fuelTypes.${option.name.toLowerCase()}`
                            )
                            : "",
                          value: option ? option.value.toString() : "",
                        };
                      }}
                      onChange={(data, selectedOption) => {
                        const fuelTypeValue = selectedOption.value
                          ? parseInt(selectedOption.value)
                          : FuelType.Unknown;
                        setVehicle({
                          fuelType: fuelTypeValue,
                          sourceFuelType: undefined, // Clear DVLA source when manually changed
                          sourceProvider: undefined,
                        });
                      }}
                    />
                  )}

                <EditableField<Booking, string>
                  inputType={EditableFieldTypes.empty}
                  columnWidth={12}
                  label={""}
                />
              </div>

              {notesForDriversFlag && (
                <div style={{ width: "100%" }}>
                  <EditableField<Booking | IBookingCreate, string>
                    inputType={EditableFieldTypes.textMultiLineFull}
                    columnWidth={12}
                    maxLength={300}
                    label={t("booking.notesForDrivers.title")}
                    placeholder={t(
                      "booking.notesForDrivers.placeholder"
                    )}
                    onProvide={() => booking.vehicle.additionalInfo || ""}
                    onChange={(data, additionalInfo) => {
                      setVehicle({ additionalInfo });
                    }}
                  />
                </div>
              )}
            </Grid>
          </EditableGroup>
        </div>
      </NotchedCard>
    </>
  );

  const outboundFlightRecordOptions =
    session.current.site.preferences.outboundFlightRecordOptions;
  const bookingCard = (
    <NotchedCard style={styles.card} label={t("booking.cards.booking")}>
      <EditableGroup<Booking | IBookingCreate>
        id="booking"
        testId="booking-group"
        editType={isBookingCardEditable}
        data={booking}
        onDataChanged={async (data) => {
          if (preventInvalidBookingDatesFlag) {
            const datesValid = await validateBookingDates(data);
            if (!datesValid) {
              return false;
            }
          }

          const saved = await validateFlightUpdate(data);

          if (saved) {
            notifyBookingDatesChanged(data);
          }

          return saved;
        }}
        onCancelEdit={() => setBooking(props.booking)}
      >
        <Grid container>
          {
            // Booking start date
            // Booking end date
            // Checked in date
            // Checked out date
            // Booking ref
            // Alt ref
            // ID
            // Imported ID
            // Package
          }

          {preferences.system_profile != SystemProfile.fleetManagement &&
            (isDatePickerReleased && isNewBookingPage ? (
              <Fragment>
                <EditableField<
                  Booking | IBookingCreate,
                  string | undefined
                >
                  useFullWidthWhenEditing
                  initializeFilled
                  inputType={EditableFieldTypes.dateTimePicker}
                  label={t("booking.labels.bookingStart")}
                  testId="booking-start-input"
                  readOnly={!canEditBookingDates}
                  showValueWhenReadOnly={true}
                  onProvide={(data) => data.bookingStartDate}
                  onChange={(data, bookingStartDate?: string) => {
                    if (bookingStartDate) {
                      handleBookingFlightDateAdjustments(bookingStartDate);
                    }
                  }}
                  defaultValue={
                    bookingStartDate?.toString() || currentDate.toString()
                  }
                  defaultMinDate={isNewBookingPage}
                  datePickerProps={{
                    selected: bookingStartDate || currentDate,
                    maxDate: preventInvalidBookingDatesFlag ? bookingEndDate : undefined,
                    onChange: () => {
                      return "";
                    },
                  }}
                />

                <EditableField<
                  Booking | IBookingCreate,
                  string | undefined
                >
                  useFullWidthWhenEditing
                  inputType={EditableFieldTypes.dateTimePicker}
                  label={t("booking.labels.bookingEnd")}
                  testId="booking-end-input"
                  readOnly={!canEditBookingDates}
                  showValueWhenReadOnly={true}
                  onProvide={(data) => data.bookingEndDate}
                  onChange={(data, bookingEndDate) => {
                    if (bookingEndDate) {
                      defaultBookingEndAppliedRef.current = false;
                      setBooking({ ...booking, bookingEndDate });
                    }
                  }}
                  defaultMinDate={isNewBookingPage}
                  datePickerProps={{
                    selected: bookingEndDate || undefined,
                    minDate: preventInvalidBookingDatesFlag ? bookingStartDate : undefined,
                    onChange: () => {
                      return "";
                    },
                  }}
                />
              </Fragment>
            ) : (
              <Fragment>
                <EditableField<Booking | IBookingCreate, string>
                  inputType={EditableFieldTypes.date}
                  label={t("booking.labels.bookingStart")}
                  testId="booking-start-input"
                  readOnly={!canEditBookingDates}
                  showValueWhenReadOnly={true}
                  onProvide={() => booking.bookingStartDate}
                  onChange={(data, bookingStartDate) => {
                    setBooking({ ...booking, bookingStartDate });
                  }}
                />

                <EditableField<Booking | IBookingCreate, string>
                  inputType={EditableFieldTypes.time}
                  label={t("input.labels.time")}
                  readOnly={!canEditBookingDates}
                  showValueWhenReadOnly={true}
                  onProvide={() => booking.bookingStartDate}
                  onChange={(data, bookingStartDate) => {
                    setBooking({ ...booking, bookingStartDate });
                  }}
                />

                <EditableField<
                  Booking | IBookingCreate,
                  string | undefined
                >
                  inputType={EditableFieldTypes.date}
                  label={t("booking.labels.bookingEnd")}
                  testId="booking-end-input"
                  readOnly={!canEditBookingDates}
                  showValueWhenReadOnly={true}
                  onProvide={() => booking.bookingEndDate}
                  onChange={(data, bookingEndDate) => {
                    if (bookingEndDate) {
                      defaultBookingEndAppliedRef.current = false;
                      setBooking({ ...booking, bookingEndDate });
                    }
                  }}
                />

                <EditableField<
                  Booking | IBookingCreate,
                  string | undefined
                >
                  inputType={EditableFieldTypes.time}
                  label={t("input.labels.time")}
                  onProvide={(data) => data.bookingEndDate}
                  onChange={(data, bookingEndDate) => {
                    if (bookingEndDate) {
                      defaultBookingEndAppliedRef.current = false;
                      setBooking({ ...booking, bookingEndDate });
                    }
                  }}
                  readOnly={isBookingEndTimeLocked || !canEditBookingDates}
                  showValueWhenReadOnly={true}
                  alwaysVisible={true}
                />
              </Fragment>
            ))}

          {!props.isNew && (
            <Fragment>
              <EditableField<Booking, string>
                readOnly={true}
                showValueWhenReadOnly={true}
                inputType={EditableFieldTypes.date}
                label={t("booking.labels.checkedIn")}
                onProvide={() =>
                  (booking as Booking).vehicle.checkInDate
                }
              />

              <EditableField<Booking, string>
                readOnly={true}
                showValueWhenReadOnly={true}
                inputType={EditableFieldTypes.date}
                label={t("booking.labels.checkedOut")}
                onProvide={() =>
                  (booking as Booking).vehicle.checkOutDate
                }
              />

              {preferences.system_profile == SystemProfile.fleetManagement && (
                <EditableField<Booking, string>
                  readOnly={true}
                  inputType={EditableFieldTypes.text}
                  label={t("input.labels.dispatchCode")}
                  onProvide={() =>
                    (booking as Booking).vehicle.additionalInfo ?? ""
                  }
                  hideReadOnlyWhenEditing={true}
                />
              )}

              {preferences.system_profile != SystemProfile.fleetManagement && (
                <EditableField<Booking, string>
                  readOnly={true}
                  inputType={EditableFieldTypes.text}
                  label={t("booking.labels.bookingRef")}
                  onProvide={() => (booking as Booking).ref}
                  hideReadOnlyWhenEditing={true}
                />
              )}
            </Fragment>
          )}

          {isRecordPropertyVisible(bookingRecordOptions, "altRef") && (
            <EditableField<Booking, string>
              inputType={EditableFieldTypes.text}
              readOnly={
                !booking.isWalkIn ||
                vehicleStatus >= VehicleStatus.CheckedIn
              }
              label={t("booking.labels.altRef")}
              required={isRecordPropertyMandatory(
                bookingRecordOptions,
                "altRef"
              )}
              onProvide={() => (booking as Booking).altRef}
              onChange={(data, altRef) =>
                setBooking({ ...booking, altRef })
              }
            />
          )}

          {!props.isNew && (
            <Fragment>
              <EditableField<Booking, number>
                readOnly={true}
                inputType={EditableFieldTypes.text}
                label={t("booking.labels.id")}
                onProvide={() => (booking as Booking).id}
              />

              {(booking as Booking).importedId && (
                <EditableField<Booking, number>
                  readOnly={true}
                  inputType={EditableFieldTypes.text}
                  label={t("booking.labels.exId")}
                  onProvide={() => (booking as Booking).importedId}
                />
              )}

              <EditableField<Booking, string>
                readOnly={true}
                inputType={EditableFieldTypes.text}
                label={t("booking.labels.packageCode")}
                onProvide={() => {
                  if (booking.packageCode == null) {
                    return "NONE";
                  }
                  if (
                    booking.packageRef == null ||
                    booking.packageRef.length == 0
                  ) {
                    return booking.packageCode;
                  }
                  return `${booking.packageCode} / ${booking.packageRef}`;
                }}
              />
            </Fragment>
          )}

          {bookingDays !== null && bookingDays > 0 && (
            <EditableField<Booking | IBookingCreate, string>
              readOnly={true}
              inputType={EditableFieldTypes.text}
              label={t("booking.labels.duration")}
              onProvide={() =>
                `${bookingDays} ${t("booking.labels.day", {
                  count: bookingDays,
                })}`
              }
            />
          )}

          {!props.isNew &&
            (booking as Booking).agentBrand &&
            isBrandNameEnabled && (
              <BookingBrandLogo
                agentCode={(booking as Booking).agentCode}
              />
            )}
        </Grid>
      </EditableGroup>
    </NotchedCard>
  );

  const outboundFlightCard = (
    <NotchedCard
      style={styles.card}
      label={isCollectionDeliveryPointsReleased ? t("booking.cards.arrivalTime") : isFlightRecordOptionsReleased ? t("booking.cards.outboundTime") : t("booking.cards.outboundFlight")}
    >
      <EditableGroup<Booking | IBookingCreate>
        id="flightOutbound"
        testId="flight-outbound-group"
        editType={editMode}
        data={booking}
        onValidate={(data) => {
          if (isFlightRecordOptionsReleased) {
            if (
              isRecordPropertyMandatory(outboundFlightRecordOptions, "flightNumber") &&
              !data.outboundFlight.flightNumber
            ) {
              setValidationErrorDialogContent({
                info: new Error(t("input.validations.noOutboundFlightNumber")),
                title: "",
              });
              return Promise.resolve(false);
            }
            if (
              isRecordPropertyMandatory(outboundFlightRecordOptions, "destination") &&
              !data.outboundFlight.flightDestination
            ) {
              setValidationErrorDialogContent({
                info: new Error(t("input.validations.noOutboundDestination")),
                title: "",
              });
              return Promise.resolve(false);
            }
            if (
              isRecordPropertyMandatory(outboundFlightRecordOptions, "terminal") &&
              !data.outboundFlight.terminal
            ) {
              setValidationErrorDialogContent({
                info: new Error(t("input.validations.noOutboundTerminal")),
                title: "",
              });
              return Promise.resolve(false);
            }
          }
          if (
            isCollectionDeliveryPointsReleased &&
            isRecordPropertyMandatory(bookingRecordOptions, "collectionPoint") &&
            !data.collectionPoint?.trim()
          ) {
            setValidationErrorDialogContent({
              info: new Error(t("input.validations.noCollectionPoint")),
              title: "",
            });
            return Promise.resolve(false);
          }
          return Promise.resolve(true);
        }}
        onDataChanged={props.saveBooking}
        onCancelEdit={() => setBooking(props.booking)}
      >
        <Grid item container>
          {isDatePickerReleased && isNewBookingPage ? (
            <EditableField<Booking | IBookingCreate, string | undefined>
              useFullWidthWhenEditing
              inputType={EditableFieldTypes.dateTimePicker}
              label={t("booking.labels.outbound")}
              testId="booking-outbound-date-input"
              onProvide={() =>
                booking.outboundFlight.flightDate || undefined
              }
              onChange={(data, flightDate) => {
                setOutboundFlight({ flightDate });
              }}
              defaultMinDate
              datePickerProps={{
                selected: outboundFlightDate || undefined,
                minDate: bookingStartDate,
                maxDate: bookingEndDate || undefined,
                onChange: () => {
                  return "";
                },
              }}
            />
          ) : (
            <>
              <EditableField<Booking | IBookingCreate, string | null>
                inputType={EditableFieldTypes.date}
                label={t("booking.labels.outboundFlightDate")}
                testId="booking-outbound-date-input"
                onProvide={() => booking.outboundFlight.flightDate}
                onChange={(data, flightDate) => {
                  setOutboundFlight({ flightDate });
                }}
              />

              <EditableField<Booking | IBookingCreate, string | undefined>
                inputType={EditableFieldTypes.time}
                label={t("input.labels.time")}
                onProvide={(data) =>
                  booking.outboundFlight.flightDate ||
                  data.bookingStartDate
                }
                onChange={(data, flightDate) => {
                  setOutboundFlight({ flightDate });
                }}
              />
            </>
          )}

          {(!isFlightRecordOptionsReleased || !outboundFlightRecordOptions ||
            isRecordPropertyVisible(outboundFlightRecordOptions, "flightNumber")) && (
              <EditableField<Booking | IBookingCreate, string>
                inputType={EditableFieldTypes.text}
                label={t("booking.labels.outboundFlightNumber")}
                required={isFlightRecordOptionsReleased && isRecordPropertyMandatory(outboundFlightRecordOptions, "flightNumber")}
                onProvide={() => booking.outboundFlight.flightNumber}
                onChange={(data, flightNumber) => {
                  setOutboundFlight({ flightNumber });
                }}
              />
            )}
          {(!isFlightRecordOptionsReleased || !outboundFlightRecordOptions ||
            isRecordPropertyVisible(outboundFlightRecordOptions, "destination")) && (
              <EditableField<Booking | IBookingCreate, string>
                inputType={EditableFieldTypes.text}
                label={t("booking.labels.outboundDestination")}
                required={isFlightRecordOptionsReleased && isRecordPropertyMandatory(outboundFlightRecordOptions, "destination")}
                onProvide={() =>
                  booking.outboundFlight.flightDestination || ""
                }
                onChange={(data, flightDestination) => {
                  setOutboundFlight({ flightDestination });
                }}
              />
            )}
          {(!isFlightRecordOptionsReleased || !outboundFlightRecordOptions ||
            isRecordPropertyVisible(outboundFlightRecordOptions, "terminal")) && (
              <EditableField<Booking | IBookingCreate, Terminal | null>
                inputType={EditableFieldTypes.terminal}
                label={t("booking.labels.outboundTerminal")}
                required={isFlightRecordOptionsReleased && isRecordPropertyMandatory(outboundFlightRecordOptions, "terminal")}
                onProvide={() => {
                  if (booking.outboundFlight.terminal == null) {
                    return booking.returnFlight.terminal;
                  }
                  return booking.outboundFlight.terminal;
                }}
                onChange={(data, terminal) => {
                  terminal && setOutboundFlight({ terminal });
                }}
              />
            )}

          {isCollectionDeliveryMeetGreetReleased && isRecordPropertyVisible(bookingRecordOptions, "confirmedArrivalDate") && isBookingObject(booking) && <>
            <EditableField<Booking, string | null>
              inputType={EditableFieldTypes.date}
              label={t("booking.labels.confirmedArrivalDate")}
              testId="booking-confirmed-arrival-date-input"
              readOnly={isPreventAmendmentArrivalAndReturnTimesReleased && vehicleStatus >= VehicleStatus.CheckedIn}
              showValueWhenReadOnly={true}
              onProvide={() => booking.confirmedArrivalDate ? new Date(booking.confirmedArrivalDate).toISOString() : null}
              onChange={(data, confirmedArrivalDate) => {
                if (confirmedArrivalDate) {
                  setConfirmedArrivalDate(new Date(confirmedArrivalDate).toISOString());
                }
              }}
            />

            <EditableField<Booking, string | null>
              inputType={EditableFieldTypes.time}
              label={t("input.labels.time")}
              testId="booking-confirmed-arrival-time-input"
              readOnly={isPreventAmendmentArrivalAndReturnTimesReleased && vehicleStatus >= VehicleStatus.CheckedIn}
              onProvide={() => booking.confirmedArrivalDate ? new Date(booking.confirmedArrivalDate).toISOString() : null}
              onChange={(data, confirmedArrivalDate) => {
                if (confirmedArrivalDate) {
                  setConfirmedArrivalDate(new Date(confirmedArrivalDate).toISOString());
                }
              }}
            />
          </>}

          {isCollectionDeliveryPointsReleased &&
            isRecordPropertyVisible(bookingRecordOptions, "collectionPoint") && (
              meetAndGreetLocations == null || meetAndGreetLocations?.collection.length ? (
                <EditableField<Booking | IBookingCreate, IDropDownListItem | null>
                  key={`collection-point-${bookingCarParkId}-${meetAndGreetLocations == null ? "loading" : "loaded"}`}
                  inputType={EditableFieldTypes.enumDropDownField}
                  label={t("booking.labels.collectionPoint")}
                  required={isRecordPropertyMandatory(bookingRecordOptions, "collectionPoint")}
                  testId="booking-collection-point-input"
                  onProvide={(data) => {
                    const value = data.collectionPoint;
                    return value ? { name: value, value } : null;
                  }}
                  onRequestText={(data) => data.collectionPoint ?? ""}
                  onRequestData={(data) =>
                    requestBookingPointOptions("collection", data.collectionPoint)
                  }
                  onChange={(data, selected) => {
                    setBooking({ ...data, collectionPoint: selected?.value || null });
                  }}
                />
              ) : (
                <EditableField<Booking | IBookingCreate, string>
                  key={`collection-point-${bookingCarParkId}`}
                  inputType={EditableFieldTypes.text}
                  label={t("booking.labels.collectionPoint")}
                  required={isRecordPropertyMandatory(bookingRecordOptions, "collectionPoint")}
                  testId="booking-collection-point-input"
                  maxLength={255}
                  onProvide={(data) => data.collectionPoint ?? ""}
                  onChange={(data, value) => {
                    setBooking({ ...data, collectionPoint: value.trim() ? value : null });
                  }}
                />
              )
            )}
        </Grid>
      </EditableGroup>
    </NotchedCard>
  );

  const returnFlightRecordOptions =
    session.current.site?.preferences.returnFlightRecordOptions;
  const returnFlightCard = (
    <NotchedCard
      style={styles.card}
      label={isFlightRecordOptionsReleased ? t("booking.cards.returnTime") : t("booking.cards.returnFlight")}
    >
      <EditableGroup<Booking | IBookingCreate>
        id="flightReturn"
        testId="flight-return-group"
        editType={editMode}
        data={booking}
        onValidate={(data) => {
          if (
            isCollectionDeliveryPointsReleased &&
            isRecordPropertyMandatory(bookingRecordOptions, "deliveryPoint") &&
            !data.deliveryPoint?.trim()
          ) {
            setValidationErrorDialogContent({
              info: new Error(t("input.validations.noDeliveryPoint")),
              title: "",
            });
            return Promise.resolve(false);
          }
          if (isFlightRecordOptionsReleased) {
            if (
              isRecordPropertyMandatory(returnFlightRecordOptions, "flightNumber") &&
              !data.returnFlight.flightNumber
            ) {
              setValidationErrorDialogContent({
                info: new Error(t("input.validations.noReturnFlightNumber")),
                title: "",
              });
              return Promise.resolve(false);
            }
            if (
              isRecordPropertyMandatory(returnFlightRecordOptions, "returningFrom") &&
              !data.returnFlight.departureAirport
            ) {
              setValidationErrorDialogContent({
                info: new Error(t("input.validations.noReturnFlightFrom")),
                title: "",
              });
              return Promise.resolve(false);
            }
            if (
              isRecordPropertyMandatory(returnFlightRecordOptions, "returnTerminal") &&
              !data.returnFlight.terminal
            ) {
              setValidationErrorDialogContent({
                info: new Error(t("input.validations.noReturnTerminal")),
                title: "",
              });
              return Promise.resolve(false);
            }
          }
          return Promise.resolve(true);
        }}
        onDataChanged={async (data) => {
          if (preventInvalidBookingDatesFlag) {
            const datesValid = await validateBookingDates(data);
            if (!datesValid) {
              return false;
            }
          }

          return validateFlightUpdate(data);
        }}
        onCancelEdit={() => setBooking(props.booking)}
      >
        <FlightLookup isNewBooking={props.isNew == true}>
          <Grid item container>
            {isDatePickerReleased && isNewBookingPage ? (
              <EditableField<Booking | IBookingCreate, string | undefined>
                useFullWidthWhenEditing
                inputType={EditableFieldTypes.dateTimePicker}
                label={t("booking.labels.returnFlight")}
                onProvide={() =>
                  booking.returnFlight.flightDate || undefined
                }
                onChange={(data, flightDate) => {
                  setReturnFlight({ flightDate });
                }}
                defaultMinDate
                datePickerProps={{
                  minDate: bookingStartDate,
                  maxDate: bookingEndDate || undefined,
                  selected:
                    returnFlightDate || bookingEndDate || undefined,
                  onChange: (date: Date) =>
                    setReturnFlight({ flightDate: date.toString() }),
                }}
              />
            ) : (
              <>
                <EditableField<Booking | IBookingCreate, string | null>
                  inputType={EditableFieldTypes.date}
                  label={t("booking.labels.returnFlightDate")}
                  onProvide={() => booking.returnFlight.flightDate}
                  onChange={(data, flightDate) => {
                    setReturnFlight({ flightDate });
                  }}
                />

                <EditableField<Booking | IBookingCreate, string | null>
                  inputType={EditableFieldTypes.time}
                  label={t("input.labels.time")}
                  onProvide={() => booking.returnFlight.flightDate}
                  onChange={(data, flightDate) => {
                    setReturnFlight({ flightDate });
                  }}
                />
              </>
            )}

            {isRecordPropertyVisible(
              returnFlightRecordOptions,
              "flightNumber"
            ) && (
                <EditableField<Booking | IBookingCreate, string>
                  inputType={EditableFieldTypes.text}
                  label={t(
                    releaseImproveFlightLookup
                      ? "booking.labels.returnFlightNumberV2"
                      : "booking.labels.returnFlightNumber"
                  )}
                  required={isRecordPropertyMandatory(
                    returnFlightRecordOptions,
                    "flightNumber"
                  )}
                  onProvide={() => booking.returnFlight.flightNumber}
                  onChange={(data, flightNumber) => {
                    setReturnFlight({ flightNumber });
                  }}
                />
              )}

            {(!isFlightRecordOptionsReleased || !returnFlightRecordOptions ||
              isRecordPropertyVisible(returnFlightRecordOptions, "returningFrom")) && (
                <EditableField<Booking | IBookingCreate, string>
                  inputType={EditableFieldTypes.text}
                  label={t("booking.labels.returnFlightFrom")}
                  required={isFlightRecordOptionsReleased && isRecordPropertyMandatory(returnFlightRecordOptions, "returningFrom")}
                  onProvide={() =>
                    booking.returnFlight.departureAirport || ""
                  }
                  onChange={(data, departureAirport) => {
                    setReturnFlight({ departureAirport });
                  }}
                />
              )}

            {(!isFlightRecordOptionsReleased || !returnFlightRecordOptions ||
              isRecordPropertyVisible(returnFlightRecordOptions, "returnTerminal")) && (
                <EditableField<Booking | IBookingCreate, Terminal | null>
                  inputType={EditableFieldTypes.terminal}
                  label={t("booking.labels.returnTerminal")}
                  required={isFlightRecordOptionsReleased && isRecordPropertyMandatory(returnFlightRecordOptions, "returnTerminal")}
                  onProvide={() => {
                    if (!booking.returnFlight.terminal) {
                      return null;
                    }
                    return booking.returnFlight.terminal;
                  }}
                  onChange={(data, terminal) => {
                    terminal && setReturnFlight({ terminal });
                  }}
                />
              )}

            {isCollectionDeliveryMeetGreetReleased && isRecordPropertyVisible(bookingRecordOptions, "confirmedReturnDate") && isBookingObject(booking) && <>
              <EditableField<Booking, string | null>
                inputType={EditableFieldTypes.date}
                label={t("booking.labels.confirmedReturnDate")}
                testId="booking-confirmed-return-date-input"
                readOnly={isPreventAmendmentArrivalAndReturnTimesReleased && vehicleStatus === VehicleStatus.CheckedOut}
                showValueWhenReadOnly={true}
                onProvide={() => booking.confirmedReturnDate ? new Date(booking.confirmedReturnDate).toISOString() : null}
                onChange={(data, confirmedReturnDate) => {
                  if (confirmedReturnDate) {
                    setConfirmedReturnDate(new Date(confirmedReturnDate).toISOString());
                  }
                }}
              />

              <EditableField<Booking, string | null>
                inputType={EditableFieldTypes.time}
                label={t("input.labels.time")}
                testId="booking-confirmed-return-time-input"
                readOnly={isPreventAmendmentArrivalAndReturnTimesReleased && vehicleStatus === VehicleStatus.CheckedOut}
                showValueWhenReadOnly={true}
                onProvide={() => booking.confirmedReturnDate ? new Date(booking.confirmedReturnDate).toISOString() : null}
                onChange={(data, confirmedReturnDate) => {
                  if (confirmedReturnDate) {
                    setConfirmedReturnDate(new Date(confirmedReturnDate).toISOString());
                  }
                }}
              />
            </>}

            {isCollectionDeliveryPointsReleased &&
              isRecordPropertyVisible(bookingRecordOptions, "deliveryPoint") && (
                meetAndGreetLocations == null || meetAndGreetLocations?.delivery.length ? (
                  <EditableField<Booking | IBookingCreate, IDropDownListItem | null>
                    key={`delivery-point-${bookingCarParkId}-${meetAndGreetLocations == null ? "loading" : "loaded"}`}
                    inputType={EditableFieldTypes.enumDropDownField}
                    label={t("booking.labels.deliveryPoint")}
                    required={isRecordPropertyMandatory(bookingRecordOptions, "deliveryPoint")}
                    testId="booking-delivery-point-input"
                    onProvide={(data) => {
                      const value = data.deliveryPoint;
                      return value ? { name: value, value } : null;
                    }}
                    onRequestText={(data) => data.deliveryPoint ?? ""}
                    onRequestData={(data) =>
                      requestBookingPointOptions("delivery", data.deliveryPoint)
                    }
                    onChange={(data, selected) => {
                      setBooking({ ...data, deliveryPoint: selected?.value || null });
                    }}
                  />
                ) : (
                  <EditableField<Booking | IBookingCreate, string>
                    key={`delivery-point-${bookingCarParkId}`}
                    inputType={EditableFieldTypes.text}
                    label={t("booking.labels.deliveryPoint")}
                    required={isRecordPropertyMandatory(bookingRecordOptions, "deliveryPoint")}
                    testId="booking-delivery-point-input"
                    maxLength={255}
                    onProvide={(data) => data.deliveryPoint ?? ""}
                    onChange={(data, value) => {
                      setBooking({ ...data, deliveryPoint: value.trim() ? value : null });
                    }}
                  />
                )
              )}

            {preferences.booking_flightValidation_enabled && (
              <EditableField<Booking | IBookingCreate, boolean>
                inputType={EditableFieldTypes.switch}
                label={t("booking.labels.returnFlightByPhone")}
                onProvide={() =>
                  Boolean(booking.returnFlight.returnByPhone)
                }
                onChange={(data, returnByPhone) => {
                  setReturnFlight({ returnByPhone });
                }}
              />
            )}
          </Grid>
        </FlightLookup>
        {
          showShuttlePickupRequest && (
            <EditableField<Booking | IBookingCreate, boolean>
              inputType={EditableFieldTypes.switch}
              label={t("settings.shuttlePickup.pickupRequested")}
              testId="booking-returnFlight-pickupRequested"
              onProvide={() => Boolean((booking as Booking).pickupRequested)}
              onChange={(data, pickupRequested) => {
                setBooking({ ...booking, pickupRequested });
              }}
            />
          )
        }
      </EditableGroup>
    </NotchedCard>
  );

  const locationsCard = (
    <NotchedCard
      style={styles.card}
      label={t("booking.cards.locations")}
      center={true}
    >
      <EditableGroup<Booking | IBookingCreate>
        id="locations"
        testId="locations-group"
        editType={editMode}
        data={booking}
        onDataChanged={async (data) => {
          let processedData = { ...data };

          if (releaseAdditionalExternalReferences && useTrackingId) {
            const previousBooking = booking as Booking;
            const validationResult = await validateTrackingIdOnSave(
              processedData as Booking
            );

            if (!validationResult) {
              // Revert UI state
              setBooking(previousBooking);
              // keep editing open and do not process movement
              return false;
            }

            processedData = { ...(validationResult as Booking) };
          }

          if (!props.movement) {
            return true;
          }

          if (isVehicleMovementAssumeKeyholderReleased) {
            // Filter out cleared locations (marked with updated: false)
            const filteredData = { ...processedData } as Booking;

            // If allocated location is marked as not updated, don't include it in the movement
            if (
              filteredData.vehicle.allocatedLane &&
              filteredData.vehicle.allocatedLane.updated === false
            ) {
              filteredData.vehicle.allocatedLane = (booking as Booking)
                .vehicle.allocatedLane;
            }

            // If vehicle location is marked as not updated, don't include it in the movement
            if (
              filteredData.vehicle.location &&
              filteredData.vehicle.location.updated === false
            ) {
              filteredData.vehicle.location = (booking as Booking).vehicle
                .location;
              filteredData.vehicle.movedBy = (booking as Booking).vehicle
                .movedBy;
            }

            // If key location is marked as not updated, don't include it in the movement
            if (
              filteredData.vehicleKey.location &&
              filteredData.vehicleKey.location.updated === false
            ) {
              filteredData.vehicleKey.location = (booking as Booking)
                .vehicleKey.location;
              filteredData.vehicleKey.movedBy = (booking as Booking)
                .vehicleKey.movedBy;
            }

            // Always validate movement now that the flag is gone
            const result = await validateMovement(
              filteredData as Booking,
              booking as Booking
            );

            // If movement was successful, reset the updated flags to allow for new changes
            if (result === true) {
              const updatedData = { ...data } as Booking;

              // Reset updated flags after successful save
              if (
                updatedData.vehicle.allocatedLane &&
                updatedData.vehicle.allocatedLane.updated === true
              ) {
                updatedData.vehicle.allocatedLane.updated = false;
              }
              if (
                updatedData.vehicle.location &&
                updatedData.vehicle.location.updated === true
              ) {
                updatedData.vehicle.location.updated = false;
              }
              if (
                updatedData.vehicleKey.location &&
                updatedData.vehicleKey.location.updated === true
              ) {
                updatedData.vehicleKey.location.updated = false;
              }

              // Update the local booking state with reset flags
              setBooking(updatedData);
            }

            return result;
          } else {
            // Original simple behavior - now always validates movement
            return validateMovement(
              processedData as Booking,
              booking as Booking
            );
          }
        }}
        onCancelEdit={() => setBooking(props.booking)}
      >
        <BookingMovement booking={props.booking as Booking}>
          {showLaneAllocationFields && (
            <Fragment>
              <EditableField<Booking, string>
                readOnly={true}
                inputType={EditableFieldTypes.text}
                label={t("booking.labels.allocatedLane")}
                testId="booking-location-allocatedLane"
                viewableStyle={{ color: "#B49DA0" }}
                onProvide={(data) => {
                  const castedBooking = data as Booking;
                  if (!castedBooking.vehicle.allocatedLane) {
                    return "";
                  }
                  if (
                    isDualParkSystemEnabled &&
                    castedBooking.vehicle.allocatedRow
                  ) {
                    return `${castedBooking.vehicle.allocatedLane?.name} (ROW ${castedBooking.vehicle.allocatedRow})`;
                  }
                  return castedBooking.vehicle.allocatedLane?.name;
                }}
              />
              <EditableField<Booking, string>
                readOnly={true}
                inputType={EditableFieldTypes.text}
                viewableStyle={{ color: "#B49DA0" }}
                label={t("booking.labels.allocatedZone")}
                testId="booking-location-allocatedZone"
                onProvide={(data) => {
                  const castedBooking = data as Booking;
                  return castedBooking.vehicle.allocatedLane == null
                    ? ""
                    : `${castedBooking.vehicle.allocatedLane.parentName ==
                      null
                      ? ""
                      : castedBooking.vehicle.allocatedLane.parentName
                    }`;
                }}
              />
              <div
                style={{
                  ...flex.flexFill,
                  marginTop: 20,
                  paddingBottom: 0,
                  borderTop: "solid 1px #eee",
                }}
              ></div>
            </Fragment>
          )}
          <EditableField<Booking, string>
            readOnly={true}
            inputType={EditableFieldTypes.text}
            label={t("booking.labels.vehicleLocation")}
            testId="booking-location-vehicleLocation"
            onProvide={(data) => {
              const castedBooking = data as Booking;
              if (!castedBooking.vehicle.location) {
                return "";
              }
              if (castedBooking.vehicle.row) {
                return `${castedBooking.vehicle.location?.name} (ROW ${castedBooking.vehicle.row})`;
              }
              return castedBooking.vehicle.location?.name;
            }}
          />

          <EditableField<Booking, string>
            readOnly={true}
            inputType={EditableFieldTypes.text}
            label={t("input.labels.zone")}
            testId="booking-location-vehicleZone"
            onProvide={(data) => {
              const castedBooking = data as Booking;
              return castedBooking.vehicle.location == null
                ? ""
                : `${castedBooking.vehicle.location.parentName == null
                  ? ""
                  : castedBooking.vehicle.location.parentName
                }`;
            }}
          />
          <EditableField<Booking, string>
            readOnly={true}
            inputType={EditableFieldTypes.text}
            label={t("booking.labels.vehicleLastMovement")}
            testId="booking-location-vehicleLastMovement"
            onProvide={(data) => {
              if (
                !(data as Booking).vehicle.location ||
                !(data as Booking).vehicle.movementDate
              ) {
                return "";
              }
              return toFormattedDateTimeString(
                new Date(data.vehicle.movementDate)
              );
            }}
          />

          <EditableField<Booking, string>
            readOnly={true}
            inputType={EditableFieldTypes.text}
            label={t("booking.labels.vehicleLastMovementBy")}
            testId="booking-location-vehicleLastMovementBy"
            onProvide={(data) => {
              const castedBooking = data as Booking;
              if (
                !castedBooking.vehicle.location ||
                !castedBooking.vehicle.movedBy
              ) {
                return "";
              }

              return `${castedBooking.vehicle.movedBy.firstName} ${castedBooking.vehicle.movedBy.lastName}`;
            }}
          />
          <div
            style={{
              ...flex.flexFill,
              marginTop: 20,
              paddingBottom: 0,
              borderTop: "solid 1px #eee",
            }}
          ></div>
          <EditableField<Booking, string>
            readOnly={true}
            inputType={EditableFieldTypes.text}
            label={t("booking.labels.keysLocation")}
            testId="booking-location-keysLocation"
            onProvide={(data) => {
              const castedBooking = data as Booking;

              if (
                !castedBooking.vehicleKey?.location ||
                (castedBooking.vehicleKey?.location?.type ===
                  AssetLocationTypes.Customer &&
                  !(data as Booking).vehicleKey.movementDate)
              ) {
                return "";
              }
              if (castedBooking.vehicleKey?.row) {
                return `${castedBooking.vehicleKey?.location.name} (ROW ${castedBooking.vehicleKey?.row})`;
              }
              return castedBooking.vehicleKey?.location.name;
            }}
          />
          <EditableField<Booking, string>
            readOnly={true}
            inputType={EditableFieldTypes.text}
            label={t("input.labels.zone")}
            testId="booking-location-keysZone"
            onProvide={(data) => {
              const castedBooking = data as Booking;
              return castedBooking.vehicleKey?.location == null
                ? ""
                : `${castedBooking.vehicleKey.location.parentName == null
                  ? ""
                  : castedBooking.vehicleKey.location.parentName
                }`;
            }}
          />
          <EditableField<Booking, string>
            readOnly={true}
            inputType={EditableFieldTypes.text}
            label={t("booking.labels.keysLastMovement")}
            testId="booking-location-keysLastMovement"
            onProvide={(data) => {
              const movementDate = (data as Booking).vehicleKey?.movementDate;
              if (!(data as Booking).vehicleKey?.location) {
                return "";
              }
              return toFormattedDateTimeString(movementDate ?? undefined);
            }}
          />

          <EditableField<Booking, string>
            readOnly={true}
            inputType={EditableFieldTypes.text}
            label={t("booking.labels.keysLastMovementBy")}
            testId="booking-location-keysLastMovementBy"
            onProvide={(data) => {
              const castedBooking = data as Booking;
              if (
                !castedBooking.vehicleKey?.location ||
                !castedBooking.vehicleKey?.movedBy
              ) {
                return "";
              }
              return `${castedBooking.vehicleKey?.movedBy.firstName} ${castedBooking.vehicleKey?.movedBy.lastName}`;
            }}
          />

          {releaseAdditionalExternalReferences && useTrackingId && (
            <>
              <Divider
                sx={{
                  ...flex.flexFill,
                  mt: 2.5,
                  pb: 0,
                  borderColor: "#eeeeee",
                }}
              />
              <Box sx={flex.flexFill}>
                <EditableField<Booking, string>
                  readOnly={false}
                  inputType={EditableFieldTypes.text}
                  columnWidth={12}
                  maxLength={10}
                  label={t("booking.labels.trackingId")}
                  testId="booking-location-trackingId"
                  onProvide={(data) => {
                    if (data.externalRefs?.length) {
                      return data.externalRefs[0].ref;
                    } else {
                      return "";
                    }
                  }}
                />
              </Box>
            </>
          )}

          <div style={{ ...flex.flexFill, marginTop: 20 }}></div>
        </BookingMovement>
      </EditableGroup>
    </NotchedCard>
  );

  const scrollToRemarks = () => {
    if (props.remarksSectionRef?.current) {
      props.remarksSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };
  const hasCustomerNotification = props.remarks?.some(
    (remark) =>
      remark?.remarkType === RemarkType.Customer && !remark?.deletedAt
  );
  const hasInternalComment = props.remarks?.some(
    (remark) =>
      remark?.remarkType === RemarkType.Internal && !remark?.deletedAt
  );

  const notifications = [
    customerRemarksBannerReleased &&
    hasCustomerNotification && (
      <NotificationBanner
        key="customer-remarks"
        message={t(
          "booking.notificationBanner.customerRemarks.message"
        )}
        onClick={scrollToRemarks}
      />
    ),
    (booking as Booking)?.hasComplaint && (
      <NotificationBanner
        key="complaints"
        message={t("booking.notificationBanner.complaints.message")}
        subMessage={t(
          "booking.notificationBanner.complaints.submessage"
        )}
        onClick={props.showComplaints}
      />
    ),
    customerRemarksBannerReleased &&
    hasInternalComment && (
      <NotificationBanner
        key="internal-remarks"
        message={t(
          "booking.notificationBanner.internalRemarks.message"
        )}
        onClick={scrollToRemarks}
      />
    ),
  ].filter(Boolean);

  return (
    <Fragment>
      <EditableContainer>
        {otherCarPark.length > 0 && (
          <Grid item xs={12} md={12}>
            <div
              style={{
                padding: 15,
                margin: 10,
                marginBottom: 5,
                borderRadius: 10,
                backgroundColor: "pink",
              }}
            >
              <b>{`${t("input.readOnly.booking")} '${otherCarPark}'.`}</b>
            </div>
          </Grid>
        )}

        {!props.isNew && !props.hideNotificationBanners && notifications.length > 0 && (
          <Grid item xs={12} md={12}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                marginBottom: "20px",
              }}
            >
              {notifications}
            </div>
          </Grid>
        )}

        {!props.isNew && props.headerPanelReleased && props.saleItems && props.salesAndPayments && props.bookingId ? (
          <Grid item xs={12} md={12} sx={{ m: "10px", mb: 5, }}>
            <BookingHeaderPanel
              booking={booking as Booking}
              saleItems={props.saleItems}
              salesAndPayments={props.salesAndPayments}
              remarks={props.remarks ?? []}
              bookingId={props.bookingId}
              onCompletedListUpdated={props.onCompletedListUpdated ?? (() => { })}
              remarksSectionRef={props.remarksSectionRef ?? { current: null }}
              paymentsSectionRef={props.paymentsSectionRef ?? { current: null }}
              onRemarkTypeChange={props.onRemarkTypeChange ?? (() => { })}
              onShowComplaints={props.showComplaints ?? (() => { })}
              readOnly={props.readOnly}
            />
          </Grid>
        ) : !props.isNew && props.headerPanelReleased && props.isLoadingSalesAndPayments ? (
          <Grid item xs={12} md={12} sx={{ m: "10px", mb: 5, }}>
            <BookingHeaderPanelSkeleton />
          </Grid>
        ) : null}

        {preferences.system_profile != SystemProfile.fleetManagement && (
          <Grid item xs={12} md={6}>
            {customerCard}
          </Grid>
        )}

        <Grid
          item
          xs={12}
          md={
            preferences.system_profile == SystemProfile.fleetManagement &&
              props.isNew == true
              ? 12
              : 6
          }
        >
          {vehicleCard}
        </Grid>
        {(preferences.system_profile != SystemProfile.fleetManagement ||
          props.isNew == false) && (
            <Grid item xs={12} md={6}>
              {bookingCard}
            </Grid>
          )}
        {vehicleStatus >= VehicleStatus.CheckedIn ? (
          <Fragment>
            <Grid
              item
              xs={12}
              md={
                preferences.system_profile == SystemProfile.fleetManagement
                  ? 12
                  : 6
              }
            >
              {locationsCard}
            </Grid>
            {preferences.booking_outboundFlight_enabled == true && (
              <Grid item xs={12} md={6}>
                {outboundFlightCard}
              </Grid>
            )}

            {preferences.booking_returnFlight_enabled == true && (
              <Grid item xs={12} md={6}>
                {returnFlightCard}
              </Grid>
            )}
          </Fragment>
        ) : preferences.booking_outboundFlight_enabled == true ? (
          <Grid item xs={12} md={6}>
            <Grid item container>
              <Grid item xs={12}>
                {outboundFlightCard}
              </Grid>

              {preferences.booking_returnFlight_enabled == true && (
                <Grid item xs={12}>
                  {returnFlightCard}
                </Grid>
              )}
            </Grid>
          </Grid>
        ) : (
          <Fragment>
            {preferences.booking_returnFlight_enabled == true && (
              <Grid item xs={12} md={6}>
                {returnFlightCard}
              </Grid>
            )}
          </Fragment>
        )}
      </EditableContainer>
      <ConfirmationDialog
        buttonCancellationText="No"
        buttonConfirmationText="Yes"
        show={showLateFlightReturnDialog.show}
        onClose={showLateFlightReturnDialog.onClose}
        title={t("input.labels.notice")}
        message={t("input.messages.bookingEnd")}
      />
      <ConfirmationDialog
        buttonCancellationText="No"
        buttonConfirmationText="Yes"
        show={showEarlyFlightReturnDialog.show}
        onClose={showEarlyFlightReturnDialog.onClose}
        title={t("input.labels.notice")}
        message={t("input.messages.flightReturns", {
          bookingFlightTimeDifference,
        })}
      />

      {/* Location overfilled confirmation now always available */}
      <ConfirmationDialog
        buttonCancellationText="No"
        buttonConfirmationText="Yes"
        show={showLocationOverfilledDialog.show}
        onClose={showLocationOverfilledDialog.onClose}
        title={t("input.labels.notice")}
        message={t("input.messages.locationFull")}
      />

      <ConfirmationDialog
        buttonCancellationText={t("general.cancel")}
        buttonConfirmationText={t("general.continue")}
        show={validationWarningDialog.show}
        onClose={validationWarningDialog.onClose}
        message={validationWarningDialogContent.info?.message || ""}
        title={validationWarningDialogContent.title}
      />
      <ErrorDialog
        err={validationErrorDialogContent?.info}
        title={validationErrorDialogContent.title}
      />
      <AlertDialog
        testId="booking-dates-changed-dialog"
        show={bookingDatesChangedDialog.show}
        onClose={() =>
          setBookingDatesChangedDialog({ show: false, message: "" })
        }
        title={t("input.labels.notice")}
        message={bookingDatesChangedDialog.message}
      />
    </Fragment>
  );
};

export default BookingFields;
